import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { BedarfDiagramme } from '../components/BedarfDiagramme';
import { ZeugnisZelle } from '../components/ZeugnisZelle';
import { api, ApiError, type BedarfDatei, type Blechbedarf, type EinkaufAngebot, type ListenAngebot, type Lieferant, type BedarfHistorie, type BedarfPapierkorb, type Artikel, type BedarfDvUebersicht, type SammelErgebnis } from '../lib/api';
import { formatOptionen, formatGewaehlt } from '../lib/formatGruppen';
import { MailModal, oeffneOutlookEntwurf, MAIL_SIGNATUR, type MailDraft } from '../components/MailEntwurf';
import { bestellPdfErzeugen, bestellPdfVorladen, type BestellPdfDaten } from '../lib/bestellPdf';
import { AutoVervollstaendigung } from '../components/AutoVervollstaendigung';
import { NeuEtikett, useEinkaufNeu } from '../components/EinkaufNeu';

type StatusFilter = 'offen' | 'angefragt' | 'angebot' | 'unbestellt' | 'bestellt' | 'fertig' | 're_offen' | 'alle';
// Jede Prozessstufe hat ihre Farbe (Chips + Zeilen-Badges identisch):
// Offen amber → Angefragt teal → Angebot da violet → Bestellt sky → Angekommen emerald
const STATUS_CHIP_AKTIV: Record<StatusFilter, string> = {
  offen: 'bg-amber-500 text-white', angefragt: 'bg-teal-600 text-white',
  angebot: 'bg-violet-600 text-white', bestellt: 'bg-sky-600 text-white',
  fertig: 'bg-emerald-600 text-white', alle: 'bg-slate-700 text-white',
  unbestellt: 'bg-orange-600 text-white', re_offen: 'bg-rose-600 text-white',
};

// deutsches Datum (DD.MM.YYYY) → ISO; alles andere unverändert lassen
function toIso(v: string): string {
  const m = v.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return v.trim();
  return `${m[3]}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
}

function fmtDatum(v: string | null): string {
  if (!v) return '—';
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
}

// Liefertermin ist Freitext ("KW17", "Ende Juli", "2026-07-05 abgehend") —
// ISO-Daten im Text nach DD.MM.YYYY wandeln, Rest unverändert lassen
function fmtTermin(v: string | null | undefined): string {
  if (!v) return '—';
  return v.replace(/(\d{4})-(\d{2})-(\d{2})/g, (_alles, j, m, t) => `${t}.${m}.${j}`);
}

// deutsches Preisformat (" 2.024,00 € ") → Zahl
function parsePreis(v: string): number | null {
  const c = v.replace(/[€\s]/g, '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(c);
  return isNaN(n) ? null : n;
}

function fmtPreis(p: number | null, einheit: string | null): string {
  if (p == null) return '—';
  const s = p.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${s} €${einheit ? ` / ${einheit}` : ''}`;
}

// Excel-Zeile (Tab-getrennt, Spaltenreihenfolge der Excel-Liste) → Datensatz
// Spalten: Datum·Kürzel·Stück·Was·Material·Format·Stärke·Wunschtermin·Kommentar/DV·
// Angefragt bei·Angefragt am·Anfrage-Notiz·Bestell-Termin·Besteller·Zeugnis·bestellt bei·
// Zugesagter LT·AB vorhanden·Preis·Einheit·Tatsächl. Lieferdatum·Tatsächl. Menge·
// Nachgefragt bei Verzug·Antwort Lieferant·Rechnungsnummer
function parseImportRow(cells: string[]): Partial<Blechbedarf> | null {
  const g = (i: number) => (cells[i] ?? '').trim();
  if (!g(0) && !g(4) && !g(8)) return null; // komplett leere Zeile
  if (/^datum$/i.test(g(0))) return null;   // Kopfzeile
  const termin = g(7);
  const wareneingang = g(20);
  const extra = [
    g(22) && `Nachgefragt bei Verzug: ${g(22)}`,
    g(23) && `Antwort Lieferant: ${g(23)}`,
  ].filter(Boolean).join(' · ');
  const bestellDatum = toIso(g(12));
  const lieferant = g(15);
  return {
    datum: toIso(g(0)) || null,
    sb: g(1) || null,
    menge: g(2) || null,
    form: g(3) || null,
    werkstoff: g(4) || null,
    format: g(5) || null,
    dicke: g(6) || null,
    termin: termin || null,
    referenz: g(8) || null,
    lieferanten_angefragt: g(9) || null,
    anfrage_datum: toIso(g(10)) || null,
    anfrage_notiz: g(11) || null,
    bestell_datum: bestellDatum || null,
    besteller: g(13) || null,
    zeugnis: g(14) || null,
    lieferant: lieferant || null,
    liefertermin: g(16) || null,
    ab_bestaetigt: g(17).toLowerCase() === 'x' ? 1 : 0,
    bestellt: bestellDatum || lieferant ? 1 : 0,
    preis: g(18) ? parsePreis(g(18)) : null,
    preis_einheit: g(19) || null,
    wareneingang: wareneingang || null,
    menge_angekommen: g(21) || null,
    bestellnr: g(24) || null,
    kommentar: extra || null,
    priority: /eilt|dringend/i.test(termin + ' ' + g(8)) ? 'eilig' : 'normal',
    fertig: wareneingang || g(21) ? 1 : 0,
  };
}

const EMPTY: Partial<Blechbedarf> = { priority: 'normal', ab_bestaetigt: 0, bestellt: 0, fertig: 0 };

// ─── Entwurf (Autosave) für „+ Neuer Bedarf" ────────────────────────────────
// Liegt nur im Browser dieses PCs (localStorage) — kein Server, der Einkauf sieht
// ihn nicht. Gespeichert wird der komplette Formularstand (`modal`): neue Felder
// sind damit automatisch dabei. Pro Benutzer + Kategorie ein Entwurf, 7 Tage gültig.
const ENTWURF_MAX_TAGE = 7;
type Entwurf = { at: string; data: Partial<Blechbedarf> };
const entwurfKey = (userId: number, kat: string) => `blechbedarf-entwurf:${userId}:${kat}`;
// Datum + SB füllt das Formular selbst; Standardwerte (0, „normal", leer) zählen nicht als Eingabe.
function entwurfHatInhalt(m: Partial<Blechbedarf>): boolean {
  return Object.entries(m).some(([k, v]) =>
    k !== 'datum' && k !== 'sb' && v !== null && v !== undefined && v !== '' && v !== 0 && v !== 'normal'
    && String(v).trim() !== '');
}
function entwurfLesen(key: string): Entwurf | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const e = JSON.parse(raw) as Entwurf;
    const alter = Date.now() - Date.parse(e.at);
    if (!e.data || !(alter < ENTWURF_MAX_TAGE * 86_400_000)) { localStorage.removeItem(key); return null; }
    return e;
  } catch { return null; }
}
function entwurfSchreiben(key: string, data: Partial<Blechbedarf>): Entwurf | null {
  try {
    const e: Entwurf = { at: new Date().toISOString(), data };
    localStorage.setItem(key, JSON.stringify(e));
    return e;
  } catch { return null; }
}
function entwurfLoeschen(key: string) {
  try { localStorage.removeItem(key); } catch { /* Speicher gesperrt — egal */ }
}

// Werkstoff → Metallgruppe (für gruppierte Auswahl im Dropdown)
const GRUPPEN_REIHENFOLGE = ['Stahl', 'Edelstahl', 'Alu', 'Kupfer', 'Sonstiges'] as const;
function werkstoffGruppe(w: string): (typeof GRUPPEN_REIHENFOLGE)[number] {
  const t = w.trim().toLowerCase();
  if (/^1\.4/.test(t)) return 'Edelstahl';
  if (/^2\.4/.test(t)) return 'Edelstahl';            // Nickelbasis (z. B. 2.4851)
  if (/^(1\.[03589]|s\d{3}|p\d{3}|e\d{3}|hardox|verzinkt|st\b)/.test(t)) return 'Stahl';
  if (/^(3\.|almg|al\s?9|alu)/.test(t)) return 'Alu';
  if (/^(2\.0|kupfer|cu\b)/.test(t)) return 'Kupfer';
  return 'Sonstiges';
}
// Professionelle Anzeige-Gruppierung nach EN 10027-2 (Werkstoffnummer):
// die Stahlgruppennummer (2 Stellen nach dem Punkt) bestimmt die Klasse.
// 00–09 Baustahl · 40–45 rostfrei · 46–49 hitzebeständig · sonst legiert;
// 3.x Alu, 2.x NE-Metall. Innerhalb jeder Gruppe nach Werkstoffnummer sortiert.
const WGRUPPEN_FEIN = [
  'Baustahl (unlegiert)',
  'Höherfester / legierter Stahl',
  'Edelstahl (rostfrei)',
  'Hitzebeständiger Stahl',
  'Aluminium',
  'Kupfer / NE-Metall',
  'Beschichtet / Sonstiges',
] as const;
function werkstoffGruppeFein(w: string): (typeof WGRUPPEN_FEIN)[number] {
  const t = w.trim().toLowerCase();
  const m = t.match(/^(\d)\.(\d)(\d)/);
  if (m) {
    if (m[1] === '3') return 'Aluminium';
    if (m[1] === '2') return 'Kupfer / NE-Metall';
    if (m[1] === '1') {
      const gr = parseInt(m[2]! + m[3]!, 10);
      if (gr <= 9) return 'Baustahl (unlegiert)';
      if (gr >= 40 && gr <= 45) return 'Edelstahl (rostfrei)';
      if (gr >= 46 && gr <= 49) return 'Hitzebeständiger Stahl';
      return 'Höherfester / legierter Stahl';
    }
  }
  if (/verzinkt|foliert|beschichtet|lochblech|hardox/.test(t)) return 'Beschichtet / Sonstiges';
  if (/edelstahl|rostfrei|v2a|v4a|^x\d/.test(t)) return 'Edelstahl (rostfrei)';
  if (/s\d{3}.*(mc|ml)|feinkorn/.test(t)) return 'Höherfester / legierter Stahl';
  if (/^(s\d{3}|p\d{3}|e\d{3}|dc|dx|st\b|c\d{2}|baustahl)/.test(t)) return 'Baustahl (unlegiert)';
  if (/almg|alsi|^alu|^al\s?\d/.test(t)) return 'Aluminium';
  if (/kupfer|^cu\b|messing|bronze/.test(t)) return 'Kupfer / NE-Metall';
  return 'Beschichtet / Sonstiges';
}
function wnrSortKey(w: string): string {
  const m = w.trim().match(/^(\d)\.(\d{4})/);
  return m ? `A${m[1]}.${m[2]}` : `Z${w.trim().toLowerCase()}`;
}
function werkstoffGruppenFein(werkstoffe: string[]): [string, string[]][] {
  const map = new Map<string, string[]>();
  for (const w of werkstoffe) {
    const g = werkstoffGruppeFein(w);
    map.set(g, [...(map.get(g) ?? []), w]);
  }
  for (const arr of map.values()) arr.sort((a, b) => wnrSortKey(a).localeCompare(wnrSortKey(b), 'de', { numeric: true }));
  return WGRUPPEN_FEIN.filter(g => map.has(g)).map(g => [g, map.get(g)!]);
}

// Auswahlliste mit "frei eingeben"-Ausweg: Standard ist ein echtes Dropdown
// (Werte aus den echten Daten); Sonderfälle bleiben als Freitext möglich.
// Optional gruppiert (optgroup), z. B. Werkstoffe nach Metall.
function SelectMitFrei({ value, onChange, optionen, gruppen, placeholder, label }: {
  value: string; onChange: (v: string) => void; optionen: string[];
  gruppen?: [string, string[]][]; placeholder?: string; label?: (v: string) => string;
}) {
  const FREI = '__frei__';
  const [freiModus, setFreiModus] = useState(false);
  const inListe = value === '' || optionen.includes(value);
  if (freiModus || !inListe) {
    return (
      <div className="flex gap-1">
        <input className="input w-full" value={value} placeholder={placeholder ?? 'frei eingeben …'}
               autoFocus={freiModus} onChange={e => onChange(e.target.value)} />
        <button type="button" className="btn-secondary px-2 shrink-0" title="Aus Liste wählen"
                onClick={() => { setFreiModus(false); if (!optionen.includes(value)) onChange(''); }}>▾</button>
      </div>
    );
  }
  return (
    <select className="input w-full" value={value}
            onChange={e => { if (e.target.value === FREI) { setFreiModus(true); onChange(''); } else onChange(e.target.value); }}>
      <option value=""></option>
      <option value={FREI}>✏️ frei eingeben …</option>
      {gruppen
        ? gruppen.map(([g, werte]) => (
            <optgroup key={g} label={g}>
              {werte.map(o => <option key={o} value={o}>{label ? label(o) : o}</option>)}
            </optgroup>
          ))
        : optionen.map(o => <option key={o} value={o}>{label ? label(o) : o}</option>)}
    </select>
  );
}

// Eingekauft wird nicht nur Blech: auch Profile/Stäbe, Rohre, Vollmaterial
// und Fittings. Gleiche Tabelle/Spalten — nur die Form-Auswahl ist gruppiert
// und Format/Dicke passen ihre Beschriftung der gewählten Form an.
const FORM_GRUPPEN: [string, string[]][] = [
  ['Blech',           ['Tafel', 'Tafeln', 'Paket', 'Zuschnitt']],
  ['Stäbe & Profile', ['Stab', 'U-Eisen', 'Winkel', 'Flachstahl', 'Vollmaterial rund', 'Sechskant']],
  ['Rohre',           ['Rundrohr', 'Vierkantrohr', 'Rechteckrohr']],
  ['Fittings',        ['Muffe', 'Stopfen', 'Nippel', 'Bogen']],
];
const FORM_OPTIONEN = FORM_GRUPPEN.flatMap(([, werte]) => werte);

type FormKategorie = 'blech' | 'profil' | 'rohr' | 'fitting';
function formKategorie(f: string): FormKategorie {
  const t = (f ?? '').trim().toLowerCase();
  if (/rohr/.test(t)) return 'rohr';
  if (/muffe|stopfen|nippel|bogen|kappe|flansch|fitting|sonstig/.test(t)) return 'fitting';
  if (/u-eisen|u-profil|stab|stang|winkel|flach|voll|sechskant|rund(?!rohr)/.test(t)) return 'profil';
  return 'blech';
}

// Welche Felder passen zur gewählten Form? Bei U-Eisen, Sechskant, Vollmaterial
// und Fittings gibt es keine Blechstärke/Wandstärke — das Feld wird ausgeblendet.
// Bewusst NICHT bei „Stab"/„Stange": im Bestand steckt dahinter oft Rohr/Profil
// mit Wandstärke (z. B. „Stab" + „Rundrohr 42,4x2" + Dicke 2). Winkel/Flachstahl
// (Stärke zählt), Rohre (Wandstärke) und Blech behalten das Feld.
const VOLLPROFIL_RE = /u-eisen|u-profil|sechskant|voll|rund(?!rohr)/;
function dickeSichtbar(form: string | null | undefined): boolean {
  const t = (form ?? '').trim().toLowerCase();
  const kat = formKategorie(t);
  if (kat === 'fitting') return false;
  if (kat === 'profil') return !VOLLPROFIL_RE.test(t);
  return true;
}

// Jede Seite zeigt nur ihr eigenes Material (Blech-Seite: nur Blech; Profile & Rohre:
// nur Stäbe/Profile + Rohre; Fittings-Seite: nur Fittings). Bestand mit anderer Form
// bleibt les-/editierbar (eigene Gruppe „Weitere (Bestand)").
const KAT_FORM_GRUPPEN: Record<string, string[]> = {
  blech: ['Blech'], profilrohr: ['Stäbe & Profile', 'Rohre'], sonstiges: ['Fittings'],
};
const KAT_FORM_KATS: Record<string, FormKategorie[]> = {
  blech: ['blech'], profilrohr: ['profil', 'rohr'], sonstiges: ['fitting'],
};

// Lieferanten-Kategorien aus dem Stamm: die Notiz beginnt mit den Gruppen
// ("Stahl + Naxtra", "Edelstahl/Sonderwerkstoffe", "Alu/ Kupfer/ Lochbleche",
// kommagetrennt, vor dem ersten "|"). Damit filtern wir die Auswahl passend
// zum Werkstoff des Bedarfs — Stahl-Lieferanten nicht bei Edelstahl-Anfragen.
type LieferantKat = 'stahl' | 'edelstahl' | 'alu';
const LIEFERANT_KAT_LABEL: Record<LieferantKat, string> = {
  stahl: 'Stahl + Naxtra', edelstahl: 'Edelstahl / Sonderwerkstoffe', alu: 'Alu / Kupfer / Lochbleche',
};
function lieferantKategorien(notiz: string | null): LieferantKat[] {
  const kopf = ((notiz ?? '').split('|')[0] ?? '').toLowerCase();
  const k: LieferantKat[] = [];
  if (/stahl\s*\+|naxtra/.test(kopf)) k.push('stahl');
  if (/edelstahl|sonderwerkstoff/.test(kopf)) k.push('edelstahl');
  if (/alu|kupfer|lochblech/.test(kopf)) k.push('alu');
  return k;
}
function werkstoffZuLieferantKat(w: string): LieferantKat | null {
  const g = werkstoffGruppe(w);
  if (g === 'Stahl') return 'stahl';
  if (g === 'Edelstahl') return 'edelstahl';
  if (g === 'Alu' || g === 'Kupfer') return 'alu';
  return null;
}

// Format-Werte (Bestand) grob derselben Kategorie zuordnen, damit das
// Format-Dropdown zur gewählten Form passt (U-Eisen → U-Profile zuerst).
function formatKategorie(f: string): FormKategorie | 'sonstig' {
  const t = f.trim().toLowerCase();
  if (/rohr/.test(t)) return 'rohr';
  if (/^u-?\s?\d|u-eisen|winkel|flach|sechskant|vollmaterial|^stab|^ipe|^hea|^heb/.test(t)) return 'profil';
  if (/dn\s?\d|muffe|stopfen|nippel|bogen|zoll|gewinde|["″]/.test(t)) return 'fitting';
  if (/^\d+\s*[x×]/.test(t) || /^(gf|maxi|mf|kf|mx)\b/.test(t) || /tafel/.test(t)) return 'blech';
  return 'sonstig';
}

// Beschriftungen für Format/Dicke je Form-Kategorie
const FORM_FELDER: Record<FormKategorie, { format: string; formatHinweis: string; dicke: string }> = {
  blech:   { format: 'Format',              formatHinweis: 'z. B. 3000×1500 / GF',      dicke: 'Dicke (mm)' },
  profil:  { format: 'Profil / Abmessung',  formatHinweis: 'z. B. U100 · 6 m / Ø60',    dicke: 'Stärke (mm)' },
  rohr:    { format: 'Abmessung (Ø×Wand)',  formatHinweis: 'z. B. 33,7×2,6 · 6 m',      dicke: 'Wandstärke (mm)' },
  fitting: { format: 'Größe / Gewinde',     formatHinweis: 'z. B. 1/2" / DN15',         dicke: 'Stärke (mm)' },
};

// aktuelle ISO-Kalenderwoche (für die Vorauswahl im KW-Modus)
function aktuelleKW(): number {
  const d = new Date();
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  const jahresanfang = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - jahresanfang.getTime()) / 86400000 + 1) / 7);
}

type TerminModus = 'datum' | 'kw' | 'frei';
function terminModus(v: string): TerminModus {
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return 'datum';
  if (!v || /^KW\s?\d{1,2}$/i.test(v)) return 'kw';
  return 'frei';
}

// Termin / KW: wählbar als Kalendertag ODER Kalenderwoche (Bestand bleibt
// als Freitext lesbar — Werte wie "eilt" oder "KW26/27" öffnen den Frei-Modus).
function TerminPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [modus, setModus] = useState<TerminModus>(() => terminModus(value));
  const kwWert = value.match(/^KW\s?(\d{1,2})$/i)?.[1] ?? '';
  const wechseln = (m: TerminModus) => {
    if (m === modus) return;
    setModus(m);
    if (m === 'datum' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) onChange('');
    if (m === 'kw' && !kwWert) onChange('');
  };
  const ToggleBtn = ({ m, label, title }: { m: TerminModus; label: string; title: string }) => (
    <button type="button" title={title} onClick={() => wechseln(m)}
            className={`px-2 py-1 text-xs rounded ${modus === m ? 'bg-brand text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-1">
      {modus === 'datum' && (
        <input type="date" className="input w-full" value={value} onChange={e => onChange(e.target.value)} />
      )}
      {modus === 'kw' && (
        <select className="input w-full" value={kwWert} onChange={e => onChange(e.target.value ? `KW${e.target.value}` : '')}>
          <option value=""></option>
          {/* ab der aktuellen Woche, danach Jahreswechsel (KW1 …) */}
          {Array.from({ length: 53 }, (_, i) => ((aktuelleKW() - 1 + i) % 53) + 1).map((n, i) => (
            <option key={n} value={n}>
              KW {n}{i === 0 ? ' (aktuell)' : n < aktuelleKW() ? ' · nächstes Jahr' : ''}
            </option>
          ))}
        </select>
      )}
      {modus === 'frei' && (
        <input className="input w-full" placeholder="z. B. eilt / KW26/27" value={value} onChange={e => onChange(e.target.value)} />
      )}
      <div className="flex gap-0.5 shrink-0">
        <ToggleBtn m="datum" label="📅" title="Kalendertag wählen" />
        <ToggleBtn m="kw" label="KW" title="Kalenderwoche wählen" />
        <ToggleBtn m="frei" label="✏️" title="Frei eingeben" />
      </div>
    </div>
  );
}

// kat: 'blech' (Standard) | 'profilrohr' | 'sonstiges' — eigene Ansichten je
// Materialkategorie, damit Bleche und U-Eisen/Rohre/Fittings nicht in einer
// Liste durcheinanderlaufen (Jasmin-Feedback).
const KAT_TITEL: Record<string, string> = {
  blech: 'Blechbedarf', profilrohr: 'Profile & Rohre', sonstiges: 'Sonstiges (Fittings)',
};
export function Blechbedarf({ kat = 'blech' }: { kat?: 'blech' | 'profilrohr' | 'sonstiges' } = {}) {
  const { daten: neuDaten, gesehen: neuGesehen } = useEinkaufNeu();
  const neuBedarfIds = new Set(neuDaten.bedarf.map(b => b.id));
  const [status, setStatus] = useState<StatusFilter>(() => {
    const p = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('status') : null;
    return p === 'bestellt' || p === 'fertig' || p === 'alle' || p === 'offen' || p === 'angefragt' || p === 'angebot' || p === 'unbestellt' || p === 're_offen' ? p : 'offen';
  });
  const [q, setQ] = useState(() =>
    typeof window !== 'undefined' ? (new URLSearchParams(window.location.search).get('q') ?? '') : '');
  // Globale Suche navigiert hierher mit ?q= / ?status= — auch ohne Remount übernehmen
  const location = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const uq = sp.get('q'); const us = sp.get('status'); const uw = sp.get('werkstoff'); const uf = sp.get('format'); const ul = sp.get('lieferant');
    if (uq !== null) { setQ(uq); setOffset(0); }
    if (uw !== null) { setFWerkstoff(uw); setOffset(0); }
    if (uf !== null) { setFFormat(uf); setOffset(0); }
    if (ul !== null) { setFLieferant(ul); setOffset(0); }
    if (us === 'bestellt' || us === 'fertig' || us === 'alle' || us === 'offen' || us === 'angefragt' || us === 'angebot' || us === 'unbestellt' || us === 're_offen') setStatus(us);
  }, [location.search]);
  const [offset, setOffset] = useState(0);
  const [typ, setTyp] = useState<'' | 'bestellung' | 'anfrage'>('');
  const [fWerkstoff, setFWerkstoff] = useState(() =>
    typeof window !== 'undefined' ? (new URLSearchParams(window.location.search).get('werkstoff') ?? '') : '');
  const [fDicke, setFDicke] = useState('');
  const [fLieferant, setFLieferant] = useState(() =>
    typeof window !== 'undefined' ? (new URLSearchParams(window.location.search).get('lieferant') ?? '') : '');
  const [fFormat, setFFormat] = useState(() =>
    typeof window !== 'undefined' ? (new URLSearchParams(window.location.search).get('format') ?? '') : '');
  const [modal, setModal] = useState<Partial<Blechbedarf> | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // Sammel-Anfrage: mehrere Positionen markieren und gemeinsam an Lieferanten anfragen
  const [wahl, setWahl] = useState<Record<number, Blechbedarf>>({});
  const [sammelAnfrageOffen, setSammelAnfrageOffen] = useState(false);
  useEffect(() => { setWahl({}); }, [kat]);
  const wahlListe = Object.values(wahl);

  const list = useApi(() => api.blechbedarf.list({ status, q, ohne_angefragt: (status === 'unbestellt' || status === 'alle') && !q.trim(), limit: 100, offset, kat, typ: typ || undefined, werkstoff: fWerkstoff || undefined, dicke: fDicke || undefined, format: fFormat || undefined, lieferant: fLieferant || undefined }), [status, q, offset, kat, typ, fWerkstoff, fDicke, fFormat, fLieferant]);
  // Zeugnis-PDFs der sichtbaren Seite (eine Abfrage für alle Zeilen)
  const sichtIds = (list.data?.data ?? []).map(r => r.id).join(',');
  const zpdf = useApi(() => sichtIds ? api.einkauf.zeugnisPdfs(sichtIds.split(',').map(Number)) : Promise.resolve({ data: {} as Record<number, string[]> }), [sichtIds]);
  const zeugnisPdfs = zpdf.data?.data ?? {};
  const lieferanten = useApi(() => api.lieferanten.list(), []);
  const users = useApi(() => api.lookup.users(), []);
  const sbKurzel = [...new Set((users.data?.data ?? []).map(u => u.kurzel).filter((k): k is string => !!k))].sort();
  const optionen = useApi(() => api.einkauf.optionen(), []);
  const me = useApi(() => api.dashboard.me(), []);
  // Storno (statt Löschen) · Papierkorb · DV-Übersicht
  const [storno, setStorno] = useState<{ id: number; grund: string } | null>(null);
  const [korbOffen, setKorbOffen] = useState(false);
  const [dvQ, setDvQ] = useState('');
  const [dvOffen, setDvOffen] = useState(false);
  const [sammelOffen, setSammelOffen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDvQ(q.trim().length >= 3 && /\d/.test(q) ? q.trim() : ''), 500);
    return () => clearTimeout(t);
  }, [q]);
  const dv = useApi(() => api.blechbedarf.dv(dvQ), [dvQ], dvQ !== '');
  const darfWiederherstellen = me.data?.user?.role === 'admin' || me.data?.user?.role === 'meister';
  const heute = () => new Date().toISOString().slice(0, 10);
  // Neuer Bedarf: Datum = heute, SB = angemeldeter Benutzer (Single-Login Portal)
  const eKey = me.data?.user?.id ? entwurfKey(me.data.user.id, kat) : null;
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);       // gespeicherter Entwurf (Badge/Banner)
  const [entwurfOffen, setEntwurfOffen] = useState(false);            // Banner: Entscheidung steht noch aus
  useEffect(() => { setEntwurf(eKey ? entwurfLesen(eKey) : null); }, [eKey]);
  const neuerBedarf = () => {
    setModal({ ...EMPTY, datum: heute(), sb: me.data?.user?.kurzel ?? '' });
    setEntwurfOffen(!!eKey && !!entwurfLesen(eKey));
  };
  // Autosave: jede Änderung am NEUEN Bedarf sofort im Browser sichern. Solange ein alter
  // Entwurf auf Entscheidung wartet, wird nichts überschrieben (kein stilles Datenverlieren).
  useEffect(() => {
    if (!eKey || !modal || modal.id || entwurfOffen) return;
    if (!entwurfHatInhalt(modal)) { entwurfLoeschen(eKey); setEntwurf(null); return; }
    const e = entwurfSchreiben(eKey, modal);
    if (e) setEntwurf(e);
  }, [modal, eKey, entwurfOffen]);
  const entwurfWiederherstellen = () => {
    const e = eKey ? entwurfLesen(eKey) : null;
    if (e) setModal({ ...EMPTY, ...e.data, id: undefined });
    setEntwurfOffen(false);
  };
  const entwurfVerwerfen = () => {
    if (eKey) entwurfLoeschen(eKey);
    setEntwurf(null); setEntwurfOffen(false);
  };
  // Tab schließen / neu laden mit ausgefülltem, ungespeichertem neuem Bedarf → Browser-Warnung
  const neuUngespeichert = !!modal && !modal.id && entwurfHatInhalt(modal);
  useEffect(() => {
    if (!neuUngespeichert) return;
    const warnen = (ev: BeforeUnloadEvent) => { ev.preventDefault(); ev.returnValue = ''; };
    window.addEventListener('beforeunload', warnen);
    return () => window.removeEventListener('beforeunload', warnen);
  }, [neuUngespeichert]);
  // Robust — greift unabhängig vom Öffnen-Weg UND wenn „me" (angemeldeter
  // Benutzer) erst nach dem Öffnen geladen ist: neuer, leerer Bedarf bekommt
  // immer Datum=heute und SB=Kürzel des angemeldeten Benutzers.
  useEffect(() => {
    if (!modal || modal.id) return;
    const k = me.data?.user?.kurzel;
    setModal(m => {
      if (!m || m.id) return m;
      const patch: Partial<Blechbedarf> = {};
      if (!m.datum) patch.datum = heute();
      if (!m.sb && k) patch.sb = k;
      return Object.keys(patch).length ? { ...m, ...patch } : m;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal?.id, !!modal, me.data]);
  const opts = optionen.data ?? { werkstoffe: [], formate: [], dicken: [], formen: [], lieferanten: [], werkstoffKurz: {} };
  // „1.0976" → „1.0976 · S355MC"; „S355MC" → „S355MC · 1.0976"; sonst unverändert.
  const wKurz = opts.werkstoffKurz ?? {};
  // Lieferant-Filter: tatsächlich genutzte Lieferanten (mit Anzahl); sonst Stamm; der aktuell gewählte bleibt immer sichtbar
  const liefFilterListe: { name: string; n?: number }[] = (() => {
    const basis = opts.lieferantenGenutzt?.length ? opts.lieferantenGenutzt : opts.lieferanten.map(name => ({ name }));
    return fLieferant && !basis.some(l => l.name.toLowerCase() === fLieferant.trim().toLowerCase()) ? [{ name: fLieferant }, ...basis] : basis;
  })();
  const wKurzRev: Record<string, string> = {};
  for (const [nr, kz] of Object.entries(wKurz)) wKurzRev[kz.toLowerCase()] = nr;
  const wLabel = (w: string): string => {
    const t = w.trim();
    if (wKurz[t]) return `${t} · ${wKurz[t]}`;
    const nr = t.match(/^(\d\.\d{4})/);
    if (nr && wKurz[nr[1]!]) return `${t} · ${wKurz[nr[1]!]}`;
    const rev = wKurzRev[t.toLowerCase()];
    if (rev) return `${t} · ${rev}`;
    return t;
  };
  // Form-Katalog + abweichende Bestandswerte als eigene Gruppe
  const katKats = KAT_FORM_KATS[kat];
  const formGruppenKat = FORM_GRUPPEN.filter(([g]) => (KAT_FORM_GRUPPEN[kat] ?? []).includes(g));
  const formExtra = opts.formen.filter(f => !FORM_OPTIONEN.includes(f) && (!katKats || katKats.includes(formKategorie(f))));
  const aktForm = (modal?.form ?? '').trim();
  const aktSichtbar = !aktForm || formGruppenKat.some(([, w]) => w.includes(aktForm)) || formExtra.includes(aktForm);
  const formExtraAlle = aktSichtbar ? formExtra : [...formExtra, aktForm];
  const formGruppen: [string, string[]][] = formExtraAlle.length
    ? [...formGruppenKat, ['Weitere (Bestand)', formExtraAlle]]
    : formGruppenKat;
  // Format-Auswahl passend zur gewählten Form: passende Werte zuerst,
  // Rest unter "Weitere" — natürlich sortiert (U-80 vor U-100 vor U-120).
  const standardKat: FormKategorie = kat === 'profilrohr' ? 'profil' : kat === 'sonstiges' ? 'fitting' : 'blech';
  const modalKat: FormKategorie = (modal?.form ?? '').trim() ? formKategorie(modal?.form ?? '') : standardKat;
  // Dicke/Stärke: auf der Profile-&-Rohre-Seite erst zeigen, wenn die gewählte Form sie braucht
  const dickeZeigen = (f: string | null | undefined, d?: string | null) =>
    (kat === 'profilrohr' && !(f ?? '').trim() && !d) ? false : dickeSichtbar(f);
  const formatOpts = formatOptionen(opts.formate.filter(f => { const k = formatKategorie(f); return k === 'sonstig' || !katKats || katKats.includes(k); }));
  const formateSortiert = [...opts.formate].sort((a, b) => a.localeCompare(b, 'de', { numeric: true }));
  const formatePassend = formateSortiert.filter(f => formatKategorie(f) === modalKat);
  const formateRest = formateSortiert.filter(f => {
    const k = formatKategorie(f);
    return k !== modalKat && (k === 'sonstig' || !katKats || katKats.includes(k));
  });
  const standardLabel = kat === 'profilrohr' ? 'Profil/Rohr' : kat === 'sonstiges' ? 'Fitting' : 'Blech';
  const formatGruppen: [string, string[]][] | undefined = formatePassend.length
    ? [[`Passend (${modal?.form || standardLabel})`, formatePassend],
       ...(formateRest.length ? [['Weitere', formateRest] as [string, string[]]] : [])]
    : (katKats ? [['Formate', formateRest]] : undefined);
  // Lieferant-Auswahl passend zum Werkstoff (Stamm-Gruppen aus der Notiz)
  const liefKat = werkstoffZuLieferantKat(modal?.werkstoff ?? '');
  const stammKats = new Map((lieferanten.data?.data ?? []).map(l => [l.name.trim().toLowerCase(), lieferantKategorien(l.notiz)] as const));
  const liefPassend = liefKat
    ? opts.lieferanten.filter(n => (stammKats.get(n.trim().toLowerCase()) ?? []).includes(liefKat))
    : [];
  const lieferantGruppen: [string, string[]][] | undefined = liefKat && liefPassend.length
    ? [[`Passend (${LIEFERANT_KAT_LABEL[liefKat]})`, liefPassend],
       ['Weitere', opts.lieferanten.filter(n => !liefPassend.includes(n))]]
    : undefined;

  async function save() {
    if (!modal) return;
    if (!(modal.referenz ?? '').trim()) {
      alert('DV-Nr ist Pflicht — bitte die DV-Nr (unsere interne Bestellnummer) eintragen.');
      return;
    }
    setBusy(true);
    try {
      if (modal.id) await api.blechbedarf.update(modal.id, modal);
      else {
        await api.blechbedarf.create(modal);
        if (eKey) { entwurfLoeschen(eKey); setEntwurf(null); }
      }
      setModal(null); list.reload();
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  }

  async function stornieren() {
    if (!storno) return;
    if (storno.grund.trim().length < 5) { alert('Bitte einen gültigen Grund angeben (mind. 5 Zeichen).'); return; }
    setBusy(true);
    try {
      await api.blechbedarf.storno(storno.id, storno.grund.trim());
      setStorno(null); setModal(null); list.reload();
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  }

  async function runImport() {
    const rows = importText.split('\n').map(l => parseImportRow(l.split('\t'))).filter(Boolean) as Partial<Blechbedarf>[];
    if (!rows.length) { setMsg('Keine gültigen Zeilen erkannt.'); return; }
    if (!confirm(`${rows.length} Zeilen importieren?`)) return;
    setBusy(true);
    try {
      const res = await api.blechbedarf.bulkImport(rows);
      setMsg(`✓ ${res.inserted} Zeilen importiert.`);
      setImportText(''); setShowImport(false); list.reload();
    } catch (e) { setMsg('Fehler: ' + (e as Error).message); }
    setBusy(false);
  }

  const set = (k: keyof Blechbedarf, v: string | number | null) => setModal(m => ({ ...m, [k]: v }));
  // Prozessstufe des offenen Modals — steuert, welche Sektionen aufgeklappt sind
  const mAngefragt = !!((modal?.lieferanten_angefragt ?? '').trim() || (modal?.anfrage_datum ?? '').trim());
  const mStufe = !modal ? 'offen' : modal.fertig ? 'fertig' : modal.bestellt ? 'bestellt'
    : (modal.angebote_da ?? 0) > 0 ? 'angebot' : mAngefragt ? 'angefragt' : 'offen';
  const mTageOhneAngebot = modal?.anfrage_datum && (modal.angebote_da ?? 0) === 0 && mStufe === 'angefragt'
    ? Math.floor((Date.now() - Date.parse(modal.anfrage_datum)) / 86_400_000) : 0;

  return (
    <div className="p-4 md:p-8">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Einkauf</div>
          <h1 className="text-2xl font-bold text-slate-900">{KAT_TITEL[kat]}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setKorbOffen(true)}
                  className="border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm font-medium hover:bg-slate-50"
                  title="Stornierte Einträge ansehen (Wiederherstellen nur Admin/Einkauf)">
            🗑 Papierkorb
          </button>
          <button onClick={() => { setMsg(''); setShowImport(true); }}
                  className="border border-slate-300 text-slate-700 rounded-lg px-4 py-2 text-sm font-medium hover:bg-slate-50">
            📋 Excel-Import
          </button>
          <button onClick={neuerBedarf}
                  className="bg-brand text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90">
            + Neuer Bedarf
            {entwurf && (
              <span className="ml-2 rounded-full bg-amber-300 text-amber-950 text-[11px] font-semibold px-2 py-0.5"
                    title="Du hast einen nicht gespeicherten Entwurf — er liegt nur in diesem Browser">
                📝 1 Entwurf offen
              </span>
            )}
          </button>
        </div>
      </header>

      {msg && <div className="mb-4 text-sm text-emerald-700">{msg}</div>}

      {/* Diagramme: Blechstärke × Format der aktuell gefilterten Liste (Klick setzt Dicke-/Format-Filter) */}
      {(kat === 'blech' || kat === 'profilrohr') && (
        <BedarfDiagramme
          filter={{ status, q, ohne_angefragt: (status === 'unbestellt' || status === 'alle') && !q.trim(), kat, typ: typ || undefined, werkstoff: fWerkstoff || undefined, lieferant: fLieferant || undefined }}
          dicke={fDicke} format={fFormat} werkstoff={fWerkstoff ? wLabel(fWerkstoff) : undefined}
          dickeName={kat === 'blech' ? 'Blechstärke' : 'Stärke'} formatName={kat === 'blech' ? 'Format' : 'Abmessung'}
          onWahl={(d, f) => { setFDicke(d ?? ''); setFFormat(f ?? ''); setOffset(0); }} />
      )}

      {/* DV-Übersicht: alles, was zu dieser DV-Nr bestellt/angefragt wurde */}
      {dvQ && dv.data?.summary && dv.data.summary.gesamt > 0 && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="dv-panel">
          <button className="w-full flex items-center justify-between flex-wrap gap-2 text-left" onClick={() => setDvOffen(o => !o)}>
            <span className="font-semibold text-emerald-900">
              DV „{dvQ}“ · {dv.data.summary.gesamt} Position{dv.data.summary.gesamt === 1 ? '' : 'en'}
            </span>
            <span className="text-sm text-emerald-800">
              {dv.data.summary.bestellt} bestellt · {dv.data.summary.fertig} angekommen · {dv.data.summary.angefragt} angefragt · {dv.data.summary.offen} offen
              {dv.data.summary.storniert > 0 && ` · ${dv.data.summary.storniert} storniert`}
              {dv.data.summary.lieferanten.length > 0 && ` · Lieferanten: ${dv.data.summary.lieferanten.join(', ')}`}
              {dvOffen ? ' ▴' : ' ▾'}
            </span>
          </button>
          {dv.data.data.some(r => !r.bestellt && !r.fertig) && (
            <div className="mt-2">
              <button data-testid="sammel-btn" onClick={() => setSammelOffen(true)}
                      className="bg-emerald-700 text-white rounded-lg px-4 py-1.5 text-sm font-medium hover:opacity-90">
                📋 Bestellung anlegen ({dv.data.data.filter(r => !r.bestellt && !r.fertig).length} offene Positionen)
              </button>
            </div>
          )}
          {dvOffen && (
            <div className="mt-3 overflow-x-auto bg-white rounded-lg border border-emerald-200">
              <table className="w-full text-[13px]">
                <thead className="bg-emerald-100 text-xs text-emerald-900 uppercase">
                  <tr>{['DV', 'Menge', 'Form', 'Werkstoff', 'Format', 'Dicke', 'Lieferant', 'Best.Nr', 'Bestellt am', 'Liefertermin', 'Status'].map(h => (
                    <th key={h} className="text-left px-2.5 py-1.5 font-medium whitespace-nowrap">{h}</th>))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {dv.data.data.map(r => (
                    <tr key={r.id} className="hover:bg-slate-50">
                      <td className="px-2.5 py-1.5 font-semibold">{r.referenz}</td>
                      <td className="px-2.5 py-1.5">{r.menge ?? '—'}</td>
                      <td className="px-2.5 py-1.5">{r.form ?? '—'}</td>
                      <td className="px-2.5 py-1.5">{r.werkstoff ?? '—'}</td>
                      <td className="px-2.5 py-1.5">{r.format ?? '—'}</td>
                      <td className="px-2.5 py-1.5">{r.dicke ?? '—'}</td>
                      <td className="px-2.5 py-1.5">{r.lieferant ?? '—'}</td>
                      <td className="px-2.5 py-1.5">{r.bestellnr ?? '—'}</td>
                      <td className="px-2.5 py-1.5 whitespace-nowrap">{fmtDatum(r.bestell_datum)}</td>
                      <td className="px-2.5 py-1.5">{r.liefertermin ?? '—'}</td>
                      <td className="px-2.5 py-1.5 whitespace-nowrap">
                        {r.fertig ? '✅ angekommen' : r.bestellt ? '📦 bestellt' : (r.lieferanten_angefragt || r.anfrage_datum) ? '✉ angefragt' : '○ offen'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Filter — direkt über der Liste und beim Scrollen oben fixiert (Hamdi 14.10.2026) */}
      <div data-testid="filterleiste" className="sticky top-0 z-20 -mx-4 md:-mx-8 px-4 md:px-8 py-2 mb-2 bg-slate-50/95 backdrop-blur border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <input placeholder="🔍 Suche …"
               value={q} onChange={e => { setQ(e.target.value); setOffset(0); }}
               className="input w-52" title="Sucht über alle Felder (Werkstoff, Referenz, Lieferant, Best.Nr, Menge, Termin, Notiz …)" />
        <select value={fWerkstoff} onChange={e => { setFWerkstoff(e.target.value); setOffset(0); }}
                className={`input text-sm w-auto max-w-[200px] ${fWerkstoff ? 'border-brand text-brand font-medium' : ''}`} title="Nach Werkstoff filtern">
          <option value="">Werkstoff ▾</option>
          {werkstoffGruppenFein(opts.werkstoffe).map(([gruppe, werte]) => (
            <optgroup key={gruppe} label={gruppe}>
              {werte.map(w => <option key={w} value={w}>{wLabel(w)}</option>)}
            </optgroup>
          ))}
        </select>
        {kat === 'blech' && (
          <select value={fDicke} onChange={e => { setFDicke(e.target.value); setOffset(0); }}
                  className={`input text-sm w-auto max-w-[140px] ${fDicke ? 'border-brand text-brand font-medium' : ''}`} title="Nach Blechstärke filtern">
            <option value="">Blechstärke ▾</option>
            {opts.dicken.map(d => <option key={d} value={d}>{d} mm</option>)}
          </select>
        )}
        <select value={formatGewaehlt(formatOpts, fFormat)} onChange={e => { setFFormat(e.target.value); setOffset(0); }}
                className={`input text-sm w-auto max-w-[160px] ${fFormat ? 'border-brand text-brand font-medium' : ''}`} title="Nach Format filtern">
          <option value="">Format ▾</option>
          {formatOpts.map(o => <option key={o.key} value={o.value}>{o.label}</option>)}
        </select>
        <select value={fLieferant} onChange={e => { setFLieferant(e.target.value); setOffset(0); }}
                className={`input text-sm w-auto max-w-[200px] ${fLieferant ? 'border-brand text-brand font-medium' : ''}`} title="Nach Lieferant filtern" data-testid="filter-lieferant">
          <option value="">Lieferant ▾</option>
          {liefFilterListe.map(l => <option key={l.name} value={l.name}>{l.name}</option>)}
        </select>
        {(fWerkstoff || fDicke || fFormat || fLieferant || q) && (
          <button onClick={() => { setFWerkstoff(''); setFDicke(''); setFFormat(''); setFLieferant(''); setQ(''); setOffset(0); }}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-4 py-2 text-sm font-semibold whitespace-nowrap shadow-sm">
            ✕ Zurücksetzen
          </button>
        )}
        <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5">
          {([['unbestellt','Unbestellt'],['offen','Offen'],['angefragt','Angefragt'],['angebot','Angebot da'],['bestellt','Bestellt'],['fertig','Angekommen'],['re_offen','RE offen'],['alle','Alle']] as const).map(([v, label]) => (
            <button key={v} onClick={() => { setStatus(v); setOffset(0); }}
                    className={`px-3 py-1.5 rounded text-sm font-medium ${
                      status === v ? STATUS_CHIP_AKTIV[v] : 'text-slate-600 hover:bg-slate-100'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5" title="Bestellung = echter Bedarf · Anfrage = reine Preisermittlung">
          {([['','Alle Arten'],['bestellung','Bestellungen'],['anfrage','Preis-Anfragen']] as const).map(([v, label]) => (
            <button key={v || 'alle'} onClick={() => { setTyp(v); setOffset(0); }}
                    className={`px-3 py-1.5 rounded text-sm font-medium ${
                      typ === v ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="text-sm text-slate-500 whitespace-nowrap ml-auto">
          {neuBedarfIds.size > 0 && <button data-testid="neu-alle-gesehen" onClick={() => neuGesehen('bedarf', [...neuBedarfIds])} className="mr-3 text-xs border border-red-300 text-red-700 rounded px-2 py-0.5 hover:bg-red-50">✓ {neuBedarfIds.size} NEU als gesehen markieren</button>}
          {list.data ? `${list.data.pagination.total} Einträge` : '…'}
          {(status === 'unbestellt' || status === 'alle') && !q.trim() && <span className="block text-[11px] text-slate-400 text-right" data-testid="ohne-angefragt-hinweis">ohne Angefragte (→ Reiter „Angefragt“)</span>}
        </div>
      </div>

      {/* Tabelle */}
      {wahlListe.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap rounded-lg border border-teal-300 bg-teal-50 px-3 py-2 text-sm" data-testid="sammel-leiste">
          <span className="font-semibold text-teal-900">{wahlListe.length} Position{wahlListe.length === 1 ? '' : 'en'} ausgewählt</span>
          {wahlListe.length > MAX_SAMMEL_ANFRAGE && <span className="text-red-700 font-medium">max. {MAX_SAMMEL_ANFRAGE} auf einmal</span>}
          <button type="button" data-testid="sammel-anfrage-btn" disabled={wahlListe.length > MAX_SAMMEL_ANFRAGE}
                  onClick={() => setSammelAnfrageOffen(true)}
                  className="bg-teal-600 text-white rounded-lg px-3 py-1.5 font-medium hover:opacity-90 disabled:opacity-40">✉ Anfrage an Lieferanten</button>
          <button type="button" className="text-slate-600 hover:underline" onClick={() => setWahl({})}>Auswahl leeren</button>
        </div>
      )}
      {sammelAnfrageOffen && (
        <SammelAnfrageModal positionen={wahlListe} lieferanten={lieferanten.data?.data ?? []}
          onClose={() => setSammelAnfrageOffen(false)}
          onFertig={(okIds) => {
            setWahl(w => { const n = { ...w }; okIds.forEach(id => delete n[id]); return n; });
            list.reload();
          }} />
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px] min-w-[1270px]">
          <thead className="bg-slate-100 border-b-2 border-slate-300 text-xs text-slate-600 uppercase">
            <tr>
              <th className="px-2 py-1.5 w-8">
                <input type="checkbox" aria-label="Alle wählbaren Positionen dieser Seite markieren" data-testid="sammel-alle"
                  checked={(() => { const w = (list.data?.data ?? []).filter(waehlbar); return w.length > 0 && w.every(r => wahl[r.id]); })()}
                  onChange={e => {
                    const w = (list.data?.data ?? []).filter(waehlbar);
                    setWahl(cur => { const n = { ...cur }; w.forEach(r => { if (e.target.checked) n[r.id] = r; else delete n[r.id]; }); return n; });
                  }} />
              </th>
              {['Datum','SB','Menge','Form','Werkstoff','Format','Dicke','Termin','Referenz','Lieferant','Liefertermin','Preis','Best.Nr','Zeugnis','Status'].map(h => (
                <th key={h} className="text-left px-2.5 py-1.5 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {list.loading && <tr><td colSpan={16} className="text-center py-12 text-slate-500">Lädt …</td></tr>}
            {list.data && list.data.data.length === 0 && (
              <tr><td colSpan={16} className="text-center py-12 text-slate-500">Keine Einträge</td></tr>
            )}
            {list.data?.data.map(r => (
              <tr key={r.id} onClick={() => { setModal({ ...r }); if (neuBedarfIds.has(r.id)) neuGesehen('bedarf', [r.id]); }}
                  className={`cursor-pointer border-l-4 ${
                    (r.bedarfstyp ?? 'bestellung') === 'anfrage'
                      ? 'border-l-violet-500 bg-violet-50 hover:bg-violet-100'
                      : 'border-l-sky-500 bg-sky-50 hover:bg-sky-100'}`}>
                <td className="px-2 py-1.5 w-8" onClick={e => e.stopPropagation()}>
                  {waehlbar(r) ? (
                    <input type="checkbox" aria-label={`Position ${r.id} für Sammel-Anfrage markieren`} checked={!!wahl[r.id]}
                      onChange={e => setWahl(cur => { const n = { ...cur }; if (e.target.checked) n[r.id] = r; else delete n[r.id]; return n; })} />
                  ) : <span className="text-slate-300" title="Schon bestellt/angekommen — nicht anfragbar">–</span>}
                </td>
                <td className="px-2.5 py-1.5 whitespace-nowrap">{neuBedarfIds.has(r.id) && <span className="mr-1.5"><NeuEtikett klein /></span>}{fmtDatum(r.datum)}</td>
                <td className="px-2.5 py-1.5">{r.sb ?? '—'}</td>
                <td className="px-2.5 py-1.5 font-semibold text-slate-800">{r.menge ?? '—'}</td>
                <td className="px-2.5 py-1.5 font-semibold text-slate-800">{r.form ?? '—'}</td>
                <td className="px-2.5 py-1.5 max-w-[140px] truncate font-semibold text-slate-800" title={r.werkstoff ?? ''}>{r.werkstoff ?? '—'}</td>
                <td className="px-2.5 py-1.5 max-w-[110px] truncate font-semibold text-slate-800" title={r.format ?? ''}>{r.format ?? '—'}</td>
                <td className="px-2.5 py-1.5 font-semibold text-slate-800">{r.dicke ?? '—'}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap font-semibold text-slate-800">
                  {fmtDatum(r.termin)}
                  {r.priority !== 'normal' && <span className="ml-1 text-xs font-bold text-red-600">{r.priority === 'kritisch' ? 'KRITISCH' : 'EILT'}</span>}
                </td>
                <td className="px-2.5 py-1.5 max-w-[170px] truncate" title={r.referenz ?? ''}>{r.referenz ?? '—'}</td>
                {!r.bestellt && !r.fertig && (r.angebote?.length ?? 0) > 0 ? (
                  <td className="px-2.5 py-1.5 min-w-[170px] max-w-[320px]" onClick={e => e.stopPropagation()}>
                    <AngebotChips r={r} lieferanten={lieferanten.data?.data ?? []} onGeaendert={() => list.reload()} />
                  </td>
                ) : (
                <td className="px-2.5 py-1.5 max-w-[160px] truncate"
                    title={r.lieferant ?? (r.lieferanten_angefragt ? `angefragt: ${r.lieferanten_angefragt}` : '')}>
                  {r.lieferant
                    ? r.lieferant
                    : r.lieferanten_angefragt
                      ? <span className="text-slate-400 italic">{r.lieferanten_angefragt} <span className="text-[10px] not-italic">(angefr.)</span></span>
                      : '—'}
                </td>
                )}
                <td className="px-2.5 py-1.5 max-w-[130px] truncate"
                    title={r.liefertermin_datum && fmtTermin(r.liefertermin) !== fmtDatum(r.liefertermin_datum)
                      ? `${fmtTermin(r.liefertermin)} → gerechnet: ${fmtDatum(r.liefertermin_datum)}`
                      : fmtTermin(r.liefertermin)}>{fmtTermin(r.liefertermin)}</td>
                <td className="px-2.5 py-1.5 whitespace-nowrap text-right">{fmtPreis(r.preis, r.preis_einheit)}</td>
                <td className="px-2.5 py-1.5 font-mono text-xs max-w-[120px] truncate" title={r.bestellnr ?? ''}>{r.bestellnr ?? '—'}</td>
                <td className="px-2 py-1" onClick={e => e.stopPropagation()}>
                  <ZeugnisZelle id={r.id} zeugnis={r.zeugnis} angekommen={!!r.fertig} pdfs={zeugnisPdfs[r.id] ?? []}
                    onGeaendert={n => { if (n.pdf) zpdf.reload(); if (n.zeugnis !== undefined) list.reload(); }} />
                </td>
                <td className="px-2.5 py-1.5">
                  <StatusBadgeBB r={r} />
                  {r.nachbedarf_am && (
                    <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[11px] font-bold whitespace-nowrap" title={r.nachbedarf_notiz ?? ''}>⚠ Nachbedarf</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {list.data && list.data.pagination.total > list.data.pagination.limit && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-sm text-slate-600">
            <div>Zeige {offset + 1}–{Math.min(offset + 100, list.data.pagination.total)} von {list.data.pagination.total}</div>
            <div className="flex gap-2">
              <button className="btn-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 100))}>‹ Zurück</button>
              <button className="btn-secondary" disabled={!list.data.pagination.hasMore} onClick={() => setOffset(offset + 100)}>Weiter ›</button>
            </div>
          </div>
        )}
      </div>

      {/* Edit-/Neu-Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">{modal.id ? `Bedarf #${modal.id} bearbeiten` : kat === 'profilrohr' ? 'Neuer Bedarf · Profile & Rohre' : kat === 'sonstiges' ? 'Neuer Bedarf · Fittings' : 'Neuer Blechbedarf'}</h2>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>
            {!modal.id && entwurfOffen && entwurf && (
              <div className="mx-6 mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-center justify-between gap-3 flex-wrap">
                <span>
                  📝 <b>Entwurf vom {new Date(entwurf.at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} Uhr gefunden</b>
                  {[entwurf.data.menge && `${entwurf.data.menge}×`, entwurf.data.form, entwurf.data.werkstoff, entwurf.data.format].filter(Boolean).length > 0
                    && ` (${[entwurf.data.menge && `${entwurf.data.menge}×`, entwurf.data.form, entwurf.data.werkstoff, entwurf.data.format].filter(Boolean).join(' ')})`}
                  <span className="block text-xs opacity-80">Automatisches Sichern ist pausiert, bis du wählst.</span>
                </span>
                <span className="flex gap-2">
                  <button type="button" onClick={entwurfWiederherstellen}
                          className="bg-amber-500 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90">Wiederherstellen</button>
                  <button type="button" onClick={entwurfVerwerfen}
                          className="border border-amber-400 text-amber-900 rounded-lg px-3 py-1.5 text-sm hover:bg-amber-100">Verwerfen</button>
                </span>
              </div>
            )}
            <div className="p-6 space-y-5">
              <StatusSchalter modal={modal} set={set} />
              <NachbedarfLeiste modal={modal} set={set} />
              <KlappSektion key={`s0-${modal.id ?? 'neu'}`} titel="Bedarf" ton="juergen"
                offenDefault={true}
                zusammenfassung={`${modal.menge ?? ''}× ${modal.form ?? ''} · ${modal.werkstoff ?? ''} ${modal.format ?? ''}${modal.dicke && dickeSichtbar(modal.form) ? ` · ${modal.dicke}mm` : ''}`}>
                <F label="" wide>
                  <ArtikelWahl kat={kat} aktuell={modal} lieferantenListe={opts.lieferanten}
                               onWahl={a => setModal(m => ({
                                 ...m,
                                 artikel_id: a.id, form: a.form ?? m?.form ?? null, werkstoff: a.werkstoff ?? m?.werkstoff ?? null,
                                 format: a.format ?? m?.format ?? null, dicke: a.dicke ?? m?.dicke ?? null,
                                 lieferanten_artikelnr: a.lieferanten_artikelnr ?? m?.lieferanten_artikelnr ?? null,
                                 lieferant: m?.lieferant || a.lieferant || null,
                               }))} />
                </F>
                <F label="Datum"><input type="date" className="input w-full" value={modal.datum ?? ''} onChange={e => set('datum', e.target.value)} /></F>
                <F label="SB (Kürzel)"><SelectMitFrei value={modal.sb ?? ''} onChange={v => set('sb', v)} optionen={sbKurzel} placeholder="z. B. JaS" /></F>
                <F label="Menge">
                  <input className="input w-full" value={modal.menge ?? ''} onChange={e => set('menge', e.target.value)} />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {[1, 2, 5, 10, 25, 50].map(n => (
                      <button type="button" key={n} onClick={() => set('menge', String(n))}
                              className="px-2 py-0.5 text-xs rounded bg-slate-100 hover:bg-slate-200 text-slate-700">{n}</button>
                    ))}
                  </div>
                </F>
                <F label="Form"><SelectMitFrei value={modal.form ?? ''} onChange={v => { set('form', v); if (!modal.id && !dickeSichtbar(v)) set('dicke', null); }}
                  optionen={[...new Set([...FORM_OPTIONEN, ...opts.formen])]} gruppen={formGruppen} placeholder="z. B. Rundrohr" /></F>
                <F label="Werkstoff"><SelectMitFrei value={modal.werkstoff ?? ''} onChange={v => set('werkstoff', v)} optionen={opts.werkstoffe} gruppen={werkstoffGruppenFein(opts.werkstoffe)} label={wLabel} placeholder="z. B. 1.4301" /></F>
                <F label={FORM_FELDER[modalKat].format}>
                  <SelectMitFrei value={modal.format ?? ''} onChange={v => set('format', v)} optionen={formateSortiert}
                    gruppen={formatGruppen} placeholder={FORM_FELDER[modalKat].formatHinweis} /></F>
                {dickeZeigen(modal.form, modal.dicke) ? (
                  <F label={FORM_FELDER[modalKat].dicke}>
                    <SelectMitFrei value={modal.dicke ?? ''} onChange={v => set('dicke', v)} optionen={opts.dicken} placeholder="z. B. 4" /></F>
                ) : modal.dicke ? (
                  <F label={FORM_FELDER[modalKat].dicke}>
                    <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-lg px-3 py-2 bg-slate-50">
                      {modal.dicke} mm gespeichert — für „{modal.form}" nicht relevant.{' '}
                      <button type="button" className="text-brand hover:underline" onClick={() => set('dicke', null)}>entfernen</button>
                    </div>
                  </F>
                ) : null}
                <F label="Termin / KW"><TerminPicker value={modal.termin ?? ''} onChange={v => set('termin', v || null)} /></F>
                <F label="DV-Nr * (Pflicht — unsere interne Bestellnummer)"><input className={`input w-full ${(modal.referenz ?? '').trim() ? '' : 'border-red-400'}`} placeholder="z. B. DV 12345" value={modal.referenz ?? ''} onChange={e => set('referenz', e.target.value)} /></F>
                <F label="Priorität">
                  <select className="input w-full" value={modal.priority ?? 'normal'} onChange={e => set('priority', e.target.value)}>
                    <option value="normal">Normal</option><option value="eilig">Eilig</option><option value="kritisch">Kritisch</option>
                  </select></F>
                <F label="Art">
                  <select className={`input w-full font-semibold ${(modal.bedarfstyp ?? 'bestellung') === 'anfrage'
                            ? 'text-violet-700 bg-violet-50 border-violet-300'
                            : 'text-sky-700 bg-sky-50 border-sky-300'}`}
                          value={modal.bedarfstyp ?? 'bestellung'} onChange={e => set('bedarfstyp', e.target.value)}>
                    <option value="bestellung">🔵 Bestellung (echter Bedarf)</option>
                    <option value="anfrage">🟣 Anfrage (nur Preisermittlung)</option>
                  </select></F>
              </KlappSektion>
              <KlappSektion key={`s1-${modal.id ?? 'neu'}`} titel="Anfrage" ton="einkauf"
                offenDefault={true}
                zusammenfassung={mAngefragt
                  ? `✓ ${modal.anfrage_datum ?? ''} · ${(modal.lieferanten_angefragt ?? '').split(',').filter(x => x.trim()).length} Lieferanten`
                  : 'noch nicht angefragt'}
                warnung={mTageOhneAngebot > 5 ? `seit ${mTageOhneAngebot}T ohne Angebot` : null}>
                <F label="Lieferanten angefragt" wide>
                  <textarea className="input w-full" rows={2} value={modal.lieferanten_angefragt ?? ''} onChange={e => set('lieferanten_angefragt', e.target.value)} /></F>
                <F label="Anfrage-Datum"><input type="date" className="input w-full" value={modal.anfrage_datum ?? ''} onChange={e => set('anfrage_datum', e.target.value)} /></F>
                <F label="Anfrage-Notiz"><input className="input w-full" value={modal.anfrage_notiz ?? ''} onChange={e => set('anfrage_notiz', e.target.value)} /></F>
                <F label="" wide>
                  {modal.id
                    ? <AnfrageMails bedarf={modal}
                        lieferanten={(lieferanten.data?.data ?? []).filter(l => l.aktiv)}
                        onAktualisiert={(neu) => { setModal({ ...neu }); list.reload(); }} />
                    : <div className="text-sm text-slate-400">Erst speichern — dann können Anfrage-Mails erzeugt werden.</div>}
                </F>
              </KlappSektion>
              {modal.id && !modal.fertig && (
                <PreisspiegelSektion bedarf={modal}
                  lieferanten={(lieferanten.data?.data ?? []).filter(l => l.aktiv)}
                  onBestellt={(neu) => { setModal({ ...neu }); list.reload(); }}
                  onKommentar={(k) => { setModal(m => (m ? { ...m, kommentar: k } : m)); list.reload(); }} />
              )}
              <KlappSektion key={`s3-${modal.id ?? 'neu'}`} titel="Bestellung" ton="einkauf"
                offenDefault={true}
                zusammenfassung={modal.bestellt
                  ? `✓ ${modal.bestell_datum ?? ''} · ${modal.lieferant ?? '—'}${modal.preis != null ? ` · ${modal.preis} €${modal.preis_einheit ? '/' + modal.preis_einheit : ''}` : ''}`
                  : 'noch nicht bestellt'}
                warnung={modal.bestellt && (!modal.lieferant || modal.preis == null) ? 'bestellt ohne Lieferant/Preis' : null}>
                <F label="Bestell-Datum"><input type="date" className="input w-full" value={modal.bestell_datum ?? ''} onChange={e => set('bestell_datum', e.target.value)} /></F>
                <F label="Besteller (Kürzel)"><input className="input w-full" value={modal.besteller ?? ''} onChange={e => set('besteller', e.target.value)} /></F>
                <F label="Zeugnis geliefert"><input className="input w-full" placeholder="ja / nein / 3.1 …" value={modal.zeugnis ?? ''} onChange={e => set('zeugnis', e.target.value)} /></F>
                <F label="Lieferant"><SelectMitFrei value={modal.lieferant ?? ''} onChange={v => set('lieferant', v)} optionen={opts.lieferanten} gruppen={lieferantGruppen} /></F>
                <F label="Lieferanten-Art.-Nr. (optional)"><input className="input w-full" placeholder="Artikelnr. beim Lieferanten" value={modal.lieferanten_artikelnr ?? ''} onChange={e => set('lieferanten_artikelnr', e.target.value || null)} /></F>
                <F label="Liefertermin"><input className="input w-full" value={modal.liefertermin ?? ''} onChange={e => set('liefertermin', e.target.value)} /></F>
                <F label="Preis"><input type="number" step="0.01" className="input w-full" value={modal.preis ?? ''} onChange={e => set('preis', e.target.value === '' ? null : Number(e.target.value))} /></F>
                <F label="Preis-Einheit">
                  <select className="input w-full" value={modal.preis_einheit ?? ''} onChange={e => set('preis_einheit', e.target.value)}>
                    <option value=""></option><option>kg</option><option>Stk</option><option>to</option><option>M</option>
                  </select></F>
                <F label="AB-/Bestell-Nr"><input className="input w-full" value={modal.bestellnr ?? ''} onChange={e => set('bestellnr', e.target.value)} /></F>
                <F label="">
                  <div className="flex gap-5 pt-2">
                    <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!modal.bestellt} onChange={e => set('bestellt', e.target.checked ? 1 : 0)} /> bestellt</label>
                    <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!modal.ab_bestaetigt} onChange={e => set('ab_bestaetigt', e.target.checked ? 1 : 0)} /> AB bestätigt</label>
                  </div></F>
              </KlappSektion>
              <KlappSektion key={`s4-${modal.id ?? 'neu'}`} titel="Wareneingang" ton="juergen"
                offenDefault={true}
                zusammenfassung={modal.fertig ? `✓ ${modal.wareneingang ?? ''}` : 'noch nicht angekommen'}>
                <F label="Angekommen am">
                  <input type="date" className="input w-full" value={/^\d{4}-\d{2}-\d{2}$/.test(modal.wareneingang ?? '') ? modal.wareneingang! : ''} onChange={e => set('wareneingang', e.target.value)} />
                  {modal.wareneingang && !/^\d{4}-\d{2}-\d{2}$/.test(modal.wareneingang) &&
                    <div className="text-xs text-slate-500 mt-1">Notiz: {modal.wareneingang} <button type="button" className="text-brand underline ml-1" onClick={() => set('wareneingang', '')}>löschen</button></div>}
                </F>
                <F label="Menge angekommen">
                  <input className="input w-full" value={modal.menge_angekommen ?? ''} onChange={e => set('menge_angekommen', e.target.value)} />
                  <div className="flex flex-wrap gap-1 mt-1">
                    {modal.menge && <button type="button" onClick={() => set('menge_angekommen', String(modal.menge))}
                            className="px-2 py-0.5 text-xs rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800">= Menge ({modal.menge})</button>}
                    {[1, 2, 5, 10, 25, 50].map(n => (
                      <button type="button" key={n} onClick={() => set('menge_angekommen', String(n))}
                              className="px-2 py-0.5 text-xs rounded bg-slate-100 hover:bg-slate-200 text-slate-700">{n}</button>
                    ))}
                  </div>
                </F>
                <F label="">
                  <label className="flex items-center gap-1.5 text-sm pt-2"><input type="checkbox" checked={!!modal.fertig} onChange={e => set('fertig', e.target.checked ? 1 : 0)} /> vollständig angekommen (fertig)</label></F>
                <F label="Kommentar" wide>
                  <textarea className="input w-full" rows={2} value={modal.kommentar ?? ''} onChange={e => set('kommentar', e.target.value)} /></F>
              </KlappSektion>
              <KlappSektion key={`sdok-${modal.id ?? 'neu'}`} titel="📎 Dokumente (AB · Angebot · Rechnung)" ton="einkauf"
                offenDefault={true}
                zusammenfassung="Bestell-PDF, AB, Angebot, Rechnung, Zeugnis …">
                <F label="" wide>
                  {modal.id
                    ? <BedarfDateien id={modal.id} />
                    : <div className="text-sm text-slate-400 pt-1">Erst speichern — danach können AB, Angebot, Rechnung, Zeugnis usw. angehängt werden.</div>}
                </F>
              </KlappSektion>
              {modal.bestellt ? (
                <RechnungSektion key={`s45-${modal.id ?? 'neu'}`} modal={modal} set={set} />
              ) : null}
              {modal.id ? (
                <KlappSektion key={`s5-${modal.id}`} titel="Verlauf" offenDefault={false}
                  zusammenfassung="wer hat wann was geändert">
                  <Verlauf id={modal.id} />
                </KlappSektion>
              ) : null}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <div>{modal.id && (
                <button onClick={() => setStorno({ id: modal.id!, grund: '' })} disabled={busy} className="text-red-600 text-sm hover:underline disabled:opacity-50">Stornieren</button>
              )}</div>
              <div className="flex gap-2">
                <button onClick={() => setModal(null)} className="btn-secondary">Abbrechen</button>
                <button onClick={save} disabled={busy}
                        className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                  {busy ? 'Speichert …' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Storno-Dialog (statt Löschen): Grund ist Pflicht */}
      {storno && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setStorno(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h2 className="font-semibold text-slate-900 mb-1">Bedarf #{storno.id} stornieren</h2>
            <p className="text-sm text-slate-500 mb-3">Der Eintrag wird nicht gelöscht, sondern in den Papierkorb verschoben und bleibt im Verlauf nachvollziehbar.</p>
            <label className="block text-xs text-slate-500 mb-1">Grund * (Pflicht)</label>
            <textarea className="input w-full" rows={3} autoFocus placeholder="z. B. Auftrag storniert / doppelt erfasst / falsches Material"
                      value={storno.grund} onChange={e => setStorno({ ...storno, grund: e.target.value })} />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setStorno(null)} className="btn-secondary">Abbrechen</button>
              <button onClick={stornieren} disabled={busy || storno.grund.trim().length < 5}
                      className="bg-red-600 text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
                {busy ? 'Storniert …' : 'Stornieren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {sammelOffen && dv.data && (
        <SammelBestellModal dvQ={dvQ} rows={dv.data.data} lieferanten={lieferanten.data?.data ?? []}
                            onClose={() => { setSammelOffen(false); list.reload(); dv.reload(); }} />
      )}

      {korbOffen && (
        <Papierkorb kat={kat} darfWiederherstellen={darfWiederherstellen}
                    onClose={() => setKorbOffen(false)} onRestored={() => list.reload()} />
      )}

      {/* Import-Modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setShowImport(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl my-8" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold text-slate-900">Excel-Import (Zeilen einfügen)</h2>
              <button onClick={() => setShowImport(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-slate-600">
                Zeilen aus der bisherigen Excel-Liste markieren, kopieren und hier einfügen
                (Tab-getrennt, Spaltenreihenfolge wie in der Liste: Datum · SB · Menge · Form · Werkstoff · Format ·
                Dicke · Termin · Referenz · Lieferanten · Anfrage-Datum · Anfrage-Notiz · Bestell-Datum · Besteller ·
                bestätigt · Lieferant · Liefertermin · x · Preis · Einheit · Wareneingang · Menge angek. · — · — · Bestell-Nr).
              </p>
              <textarea className="input w-full font-mono text-xs" rows={12} placeholder="Hier einfügen …"
                        value={importText} onChange={e => setImportText(e.target.value)} />
              <div className="text-sm text-slate-500">
                {importText.trim() ? `${importText.split('\n').filter(l => l.trim()).length} Zeilen erkannt` : ''}
              </div>
              {msg && <div className="text-sm text-red-600">{msg}</div>}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setShowImport(false)} className="btn-secondary">Abbrechen</button>
              <button onClick={runImport} disabled={busy || !importText.trim()}
                      className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {busy ? 'Importiert …' : 'Importieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Bestell-Mail zum gewählten Angebot — öffnet nach dem Bestellen automatisch
// den Mail-Client (Outlook) mit fertigem Bestelltext.
function bestellMail(bedarf: Partial<Blechbedarf>, a: EinkaufAngebot, email: string): MailDraft {
  const mat = `${bedarf.menge ?? ''}x ${bedarf.werkstoff ?? ''} ${bedarf.format ?? ''}${bedarf.dicke ? ` · ${bedarf.dicke} mm` : ''}`.trim();
  const ref = bedarf.referenz ? ` (${bedarf.referenz})` : '';
  const zeilen = [
    `Menge: ${bedarf.menge ?? '—'} ${bedarf.form ?? ''}`.trim(),
    `Werkstoff: ${bedarf.werkstoff ?? '—'}`,
    `Format: ${bedarf.format ?? '—'}${bedarf.dicke ? ` · Dicke ${bedarf.dicke} mm` : ''}`,
    a.preis != null ? `Preis: ${a.preis.toLocaleString('de-DE')} €${a.preis_einheit ? `/${a.preis_einheit}` : ''} (lt. Ihrem Angebot)` : null,
    a.liefertermin ? `Liefertermin: ${a.liefertermin}` : null,
    bedarf.referenz ? `Unsere Referenz: ${bedarf.referenz}` : null,
  ].filter(Boolean);
  return {
    an: email,
    betreff: `Bestellung – ${mat}${ref}`,
    text: 'Guten Tag,\n\nhiermit bestellen wir wie angeboten:\n\n' +
      zeilen.map(z => `  · ${z}`).join('\n') +
      '\n\nBitte senden Sie uns die Auftragsbestätigung mit verbindlichem Liefertermin' +
      ' und legen Sie das Materialzeugnis bei.' +
      '\n\nVielen Dank.\n\n' + MAIL_SIGNATUR,
  };
}

// ---- Sammel-Anfrage (mehrere Bedarfe auf einmal) --------------------------------
const MAX_SAMMEL_ANFRAGE = 50;
// nur Positionen, die noch nicht bestellt/angekommen sind, lassen sich anfragen
const waehlbar = (r: Blechbedarf) => !r.bestellt && !r.fertig;

function positionsZeile(p: Partial<Blechbedarf>, nr: number): string {
  const teile = [
    [p.menge, p.form].filter(Boolean).join(' '),
    p.werkstoff,
    p.format ? `${p.format}${p.dicke ? ` · ${p.dicke} mm` : ''}` : p.dicke ? `${p.dicke} mm` : '',
    p.termin ? `Termin ${p.termin}` : '',
  ].filter(x => x && String(x).trim());
  return `Pos. ${nr}: ${teile.join(' · ')}`;
}

// Eine Anfrage-Mail je Lieferant mit allen Positionen
export function sammelAnfrageMail(positionen: Partial<Blechbedarf>[], email: string): MailDraft {
  return {
    an: email,
    betreff: `Anfrage – ${positionen.length} Position${positionen.length === 1 ? '' : 'en'}`,
    text: anfrageText(positionen.map((p, i) => positionsZeile(p, i + 1))),
  };
}

export function SammelAnfrageModal({ positionen, lieferanten, onClose, onFertig }: {
  positionen: Blechbedarf[]; lieferanten: Lieferant[]; onClose: () => void; onFertig: (okIds: number[]) => void;
}) {
  const [auswahl, setAuswahl] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState<{ ok: number; fehler: { id: number; grund: string }[]; entwuerfe: number } | null>(null);
  const toggle = (id: number) => setAuswahl(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const kats = new Set(positionen.map(p => werkstoffZuLieferantKat(p.werkstoff ?? '')).filter(Boolean) as LieferantKat[]);
  const sektionen: [string, Lieferant[]][] = (() => {
    const reihen = (['stahl', 'edelstahl', 'alu'] as LieferantKat[])
      .sort((a, b) => Number(kats.has(b)) - Number(kats.has(a)))
      .map(k => [LIEFERANT_KAT_LABEL[k] + (kats.has(k) ? ' (passt zur Auswahl)' : ''), lieferanten.filter(l => lieferantKategorien(l.notiz).includes(k))] as [string, Lieferant[]]);
    const ohne = lieferanten.filter(l => lieferantKategorien(l.notiz).length === 0);
    if (ohne.length) reihen.push(['Ohne Zuordnung', ohne]);
    return reihen.filter(([, ls]) => ls.length > 0);
  })();

  async function erzeugen() {
    const gewaehlt = lieferanten.filter(l => auswahl.has(l.id));
    if (!gewaehlt.length || positionen.length === 0 || positionen.length > MAX_SAMMEL_ANFRAGE) return;
    setBusy(true);
    const heute = new Date().toISOString().slice(0, 10);
    const okPos: Blechbedarf[] = [];
    const fehler: { id: number; grund: string }[] = [];
    // 1) Erst speichern (je Position), 2) dann Entwürfe nur für gespeicherte Positionen
    for (const p of positionen) {
      try {
        const vorhanden = new Set(((await api.einkauf.angebote(p.id)).data ?? []).map(a => a.lieferant.trim().toLowerCase()));
        for (const l of gewaehlt) {
          if (!vorhanden.has(l.name.trim().toLowerCase())) await api.einkauf.angebotAnlegen(p.id, { lieferant: l.name, status: 'offen' });
        }
        const bisher = (p.lieferanten_angefragt ?? '').trim();
        const neu = gewaehlt.map(l => l.name).filter(n => !bisher.toLowerCase().includes(n.toLowerCase()));
        await api.blechbedarf.update(p.id, {
          lieferanten_angefragt: [bisher, neu.join(', ')].filter(Boolean).join(', '),
          anfrage_datum: p.anfrage_datum || heute,
        });
        okPos.push(p);
      } catch (e) { fehler.push({ id: p.id, grund: (e as Error).message }); }
    }
    let entwuerfe = 0;
    if (okPos.length) {
      for (const [i, l] of gewaehlt.entries()) {
        oeffneOutlookEntwurf(sammelAnfrageMail(okPos, l.email ?? ''));
        entwuerfe++;
        if (i < gewaehlt.length - 1) await new Promise(r => setTimeout(r, 400));
      }
    }
    setErgebnis({ ok: okPos.length, fehler, entwuerfe });
    onFertig(okPos.map(p => p.id));
    setBusy(false);
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()} data-testid="sammel-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">✉ Sammel-Anfrage — {positionen.length} Position{positionen.length === 1 ? '' : 'en'}</h2>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-4">
          {ergebnis ? (
            <div className="space-y-2" data-testid="sammel-ergebnis">
              <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                ✓ {ergebnis.ok} Position{ergebnis.ok === 1 ? '' : 'en'} als angefragt gespeichert · {ergebnis.entwuerfe} Outlook-Entwurf{ergebnis.entwuerfe === 1 ? '' : 'e'} erzeugt (Versand bitte in Outlook).
              </div>
              {ergebnis.fehler.length > 0 && (
                <div className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {ergebnis.fehler.length} Position{ergebnis.fehler.length === 1 ? '' : 'en'} nicht gespeichert (nicht in den Mails):
                  <ul className="list-disc pl-5">{ergebnis.fehler.map(f => <li key={f.id}>#{f.id}: {f.grund}</li>)}</ul>
                </div>
              )}
              <div className="flex justify-end"><button className="btn-secondary" onClick={onClose}>Schließen</button></div>
            </div>
          ) : (
            <>
              <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100 text-sm">
                {positionen.map((p, i) => <div key={p.id} className="px-3 py-1.5">{positionsZeile(p, i + 1)}</div>)}
              </div>
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 space-y-2">
                <div className="text-sm font-medium text-slate-700">Lieferanten auswählen — je Lieferant ein Outlook-Entwurf mit allen Positionen:</div>
                <div className="max-h-52 overflow-y-auto space-y-2">
                  {sektionen.map(([titel, ls]) => (
                    <div key={titel}>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">{titel}</div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                        {ls.map(l => (
                          <label key={`${titel}-${l.id}`} className="flex items-center gap-1.5 text-sm">
                            <input type="checkbox" checked={auswahl.has(l.id)} onChange={() => toggle(l.id)} />
                            <span className="truncate">{l.name}{!l.email && ' ⚠'}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-slate-500">⚠ = keine E-Mail im Lieferantenstamm — Entwurf öffnet ohne Empfänger. Je Position werden „Lieferanten angefragt“, Anfrage-Datum und eine offene Preisspiegel-Zeile je Lieferant nachgetragen. Es wird keine Mail automatisch versendet.</div>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Abbrechen</button>
                <button type="button" data-testid="sammel-erzeugen" onClick={erzeugen} disabled={busy || auswahl.size === 0}
                        className="bg-brand text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
                  {busy ? 'Speichere & erzeuge …' : `📧 ${auswahl.size} Entwurf${auswahl.size === 1 ? '' : 'e'} erzeugen`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Anfrage-Text (Wunsch Jasmin 08.10.2026): feste Anrede/Einleitung/Schluss, nur die Positionen variieren.
// Interne Referenzen stehen bewusst NICHT in der Mail an den Lieferanten.
// zusatz: optionale Hinweise unter der Positionsliste (z. B. „Wir benötigen immer Zeugnisse“).
export function anfrageText(positionenZeilen: string[], zusatz: string[] = []): string {
  return 'Sehr geehrte Damen und Herren,\n\n' +
    'hiermit möchte ich gerne anfragen, ob folgende Produkte aktuell verfügbar sind und zu welchen Konditionen (Preis, Lieferzeit und Versandkosten).\n\n' +
    positionenZeilen.map(z => `  · ${z}`).join('\n') +
    (zusatz.length ? '\n\n' + zusatz.join('\n') : '') +
    '\n\nÜber die Zusendung eines entsprechenden Angebots würde ich mich freuen.\n' +
    'Für Rückfragen stehe ich Ihnen jederzeit gerne zur Verfügung.\n\n' +
    'Vielen Dank im Voraus.\n\n' + MAIL_SIGNATUR;
}

// Anfrage-Mail an einen Lieferanten (Outlook-Entwurf mit Logo)
function anfrageMail(bedarf: Partial<Blechbedarf>, email: string): MailDraft {
  const zeilen = [
    bedarf.menge ? `Menge: ${bedarf.menge}${bedarf.form ? ' ' + bedarf.form : ''}` : null,
    bedarf.werkstoff ? `Werkstoff: ${bedarf.werkstoff}` : null,
    bedarf.format ? `Format/Abmessung: ${bedarf.format}${bedarf.dicke ? ` · ${bedarf.dicke} mm` : ''}` : null,
    bedarf.termin ? `Gewünschter Termin: ${bedarf.termin}` : null,
  ].filter(Boolean) as string[];
  return {
    an: email,
    betreff: `Anfrage – ${bedarf.menge ?? ''}x ${bedarf.werkstoff ?? ''} ${bedarf.format ?? ''}${bedarf.dicke ? ` · ${bedarf.dicke} mm` : ''}`.trim(),
    text: anfrageText(zeilen),
  };
}

// Anfrage-Mails an mehrere Lieferanten: pro Auswahl ein Outlook-Entwurf (.eml),
// trägt "Lieferanten angefragt" + Anfrage-Datum nach und legt je Lieferant
// eine offene Preisspiegel-Zeile an.
function AnfrageMails({ bedarf, lieferanten, onAktualisiert }: {
  bedarf: Partial<Blechbedarf>; lieferanten: Lieferant[]; onAktualisiert: (neu: Blechbedarf) => void;
}) {
  const [auswahl, setAuswahl] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [alleAnzeigen, setAlleAnzeigen] = useState(false);
  const bedarfId = bedarf.id as number;
  const toggle = (id: number) => setAuswahl(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Passend zum Werkstoff filtern (Stamm-Gruppen aus der Notiz); umschaltbar auf alle.
  const kat = werkstoffZuLieferantKat(bedarf.werkstoff ?? '');
  const passende = kat ? lieferanten.filter(l => lieferantKategorien(l.notiz).includes(kat)) : lieferanten;
  const zeigeAlle = alleAnzeigen || !kat || passende.length === 0;
  // Immer nach Stamm-Gruppen gegliedert; im Filter-Modus nur die passende Gruppe.
  const sektionen: [string, Lieferant[]][] = (() => {
    if (!zeigeAlle && kat) return [[LIEFERANT_KAT_LABEL[kat], passende]];
    const reihen: [string, Lieferant[]][] = (['stahl', 'edelstahl', 'alu'] as LieferantKat[]).map(k =>
      [LIEFERANT_KAT_LABEL[k], lieferanten.filter(l => lieferantKategorien(l.notiz).includes(k))]);
    const ohne = lieferanten.filter(l => lieferantKategorien(l.notiz).length === 0);
    if (ohne.length) reihen.push(['Ohne Zuordnung', ohne]);
    if (kat) reihen.sort((a, b) => Number(b[0] === LIEFERANT_KAT_LABEL[kat]) - Number(a[0] === LIEFERANT_KAT_LABEL[kat]));
    return reihen.filter(([, ls]) => ls.length > 0);
  })();

  async function erzeugen() {
    const gewaehlt = lieferanten.filter(l => auswahl.has(l.id));
    if (!gewaehlt.length) return;
    setBusy(true);
    try {
      // bestehende Preisspiegel-Zeilen nicht doppeln
      const vorhanden = new Set(((await api.einkauf.angebote(bedarfId)).data ?? [])
        .map(a => a.lieferant.trim().toLowerCase()));
      for (const [i, l] of gewaehlt.entries()) {
        oeffneOutlookEntwurf(anfrageMail(bedarf, l.email ?? ''));
        if (!vorhanden.has(l.name.trim().toLowerCase())) {
          await api.einkauf.angebotAnlegen(bedarfId, { lieferant: l.name, status: 'offen' });
        }
        if (i < gewaehlt.length - 1) await new Promise(r => setTimeout(r, 400));
      }
      const bisher = (bedarf.lieferanten_angefragt ?? '').trim();
      const neueNamen = gewaehlt.map(l => l.name).filter(n => !bisher.toLowerCase().includes(n.toLowerCase()));
      const r = await api.blechbedarf.update(bedarfId, {
        lieferanten_angefragt: [bisher, neueNamen.join(', ')].filter(Boolean).join(', '),
        anfrage_datum: bedarf.anfrage_datum ?? new Date().toISOString().slice(0, 10),
      });
      onAktualisiert(r.data);
      setAuswahl(new Set());
    } catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  }

  // Panel ist dauerhaft sichtbar (Hamdi: „zwei Nummer devamlı açık olsun“) —
  // der frühere Aufklapp-Link entfällt.
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 space-y-2">
      <div className="text-sm font-medium text-slate-700 flex items-center justify-between flex-wrap gap-1">
        <span>Lieferanten auswählen — je Auswahl ein Outlook-Entwurf (mit Logo):</span>
        {kat && passende.length > 0 && (
          <button type="button" onClick={() => setAlleAnzeigen(a => !a)} className="text-xs text-brand hover:underline font-normal">
            {zeigeAlle ? `nur ${LIEFERANT_KAT_LABEL[kat]} zeigen` : `gefiltert: ${LIEFERANT_KAT_LABEL[kat]} — alle ${lieferanten.length} anzeigen`}
          </button>
        )}
      </div>
      <div className="max-h-52 overflow-y-auto space-y-2">
        {sektionen.map(([titel, ls]) => (
          <div key={titel}>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">{titel}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
              {ls.map(l => (
                <label key={`${titel}-${l.id}`} className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" checked={auswahl.has(l.id)} onChange={() => toggle(l.id)} />
                  <span className="truncate" title={((l.notiz ?? '').split('|')[0] ?? '').trim() || undefined}>
                    {l.name}{!l.email && ' ⚠'}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="text-xs text-slate-500">⚠ = keine E-Mail im Lieferantenstamm — Entwurf öffnet ohne Empfänger. „Lieferanten angefragt" und Anfrage-Datum werden automatisch nachgetragen, der Preisspiegel bekommt je Lieferant eine offene Zeile.</div>
      <div className="flex gap-2 justify-end">
        <button type="button" className="btn-secondary" onClick={() => setAuswahl(new Set())}>Auswahl leeren</button>
        <button type="button" onClick={erzeugen} disabled={busy || auswahl.size === 0}
                className="bg-brand text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
          {busy ? 'Erzeuge …' : `📧 ${auswahl.size} Entwurf${auswahl.size === 1 ? '' : 'e'} erzeugen`}
        </button>
      </div>
    </div>
  );
}

// Preisspiegel (E2): Angebote je Bedarf erfassen, vergleichen, per Klick bestellen.
// ======================================================
// Angebote je Zeile direkt in der Liste: wer hat angeboten / abgesagt / ist noch offen — und von hier bestellen.
// (Jasmin: Ein Lieferant hat oft nicht alle Positionen einer Sammelanfrage.)
// ======================================================
const ANGEBOT_CHIP: Record<ListenAngebot['status'], { cls: string; icon: string; titel: string }> = {
  offen:    { cls: 'bg-amber-100 text-amber-800 border-amber-300',       icon: '⏳', titel: 'angefragt — noch keine Antwort' },
  erhalten: { cls: 'bg-violet-100 text-violet-800 border-violet-300',    icon: '✓',  titel: 'hat angeboten' },
  absage:   { cls: 'bg-slate-100 text-slate-400 border-slate-200 line-through', icon: '✗', titel: 'Absage' },
};

export function AngebotChips({ r, lieferanten, onGeaendert }: {
  r: Blechbedarf; lieferanten: Lieferant[]; onGeaendert: () => void;
}) {
  const [offen, setOffen] = useState<ListenAngebot | null>(null);
  const [mail, setMail] = useState<MailDraft | null>(null);
  const liste = r.angebote ?? [];
  const beste = Math.min(...liste.filter(a => a.status === 'erhalten' && a.preis != null).map(a => a.preis as number));
  return (
    <>
      <div className="flex flex-wrap gap-1" data-testid={`angebot-chips-${r.id}`}>
        {liste.map(a => (
          <button key={a.id} type="button" data-testid={`angebot-chip-${a.id}`} onClick={() => setOffen(a)}
                  title={`${a.lieferant}: ${ANGEBOT_CHIP[a.status].titel} — Klick zum Bearbeiten/Bestellen`}
                  className={`border rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap hover:shadow ${ANGEBOT_CHIP[a.status].cls}`}>
            {ANGEBOT_CHIP[a.status].icon} {a.lieferant}
            {a.status === 'erhalten' && a.preis != null && <> · {a.preis.toLocaleString('de-DE')}{a.preis_einheit ? `/${a.preis_einheit}` : ' €'}{a.preis === beste && liste.filter(x => x.status === 'erhalten' && x.preis != null).length > 1 ? ' ★' : ''}</>}
          </button>
        ))}
      </div>
      {offen && (
        <AngebotPopup r={r} a={offen} lieferanten={lieferanten}
                      onClose={() => setOffen(null)}
                      onGeaendert={onGeaendert}
                      onMail={m => { setOffen(null); setMail(m); }} />
      )}
      {mail && <MailModal draft={mail} onChange={setMail} onClose={() => setMail(null)} />}
    </>
  );
}

function AngebotPopup({ r, a, lieferanten, onClose, onGeaendert, onMail }: {
  r: Blechbedarf; a: ListenAngebot; lieferanten: Lieferant[];
  onClose: () => void; onGeaendert: () => void; onMail: (m: MailDraft) => void;
}) {
  const [status, setStatus] = useState<ListenAngebot['status']>(a.status);
  const [preis, setPreis] = useState(a.preis != null ? String(a.preis) : '');
  const [einheit, setEinheit] = useState(a.preis_einheit ?? '');
  const [termin, setTermin] = useState(a.liefertermin ?? '');
  const [notiz, setNotiz] = useState(a.notiz ?? '');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const felder = (): Partial<EinkaufAngebot> => ({
    status, preis: preis.trim() === '' ? null : Number(preis.replace(',', '.')),
    preis_einheit: einheit || null, liefertermin: termin.trim() || null, notiz: notiz.trim() || null,
  });
  const speichern = async (): Promise<EinkaufAngebot | null> => {
    setBusy(true); setFehler('');
    try { const res = await api.einkauf.angebotUpdate(a.id, felder()); onGeaendert(); return res.data; }
    catch (e) { setFehler((e as Error).message); return null; }
    finally { setBusy(false); }
  };
  const bestellen = async () => {
    const f = felder();
    if (f.preis != null && Number.isNaN(f.preis)) { setFehler('Preis ist keine Zahl'); return; }
    const gespeichert = await speichern();
    if (!gespeichert) return;
    if (!confirm(`Bei ${a.lieferant} bestellen?\n${f.preis != null ? `${f.preis} €${f.preis_einheit ? '/' + f.preis_einheit : ''} · ` : ''}LT: ${f.liefertermin ?? '—'}`)) return;
    setBusy(true);
    try {
      await api.einkauf.bestellen(r.id, a.id);
      onGeaendert();
      // gleiche Bestell-Mail wie im Preisspiegel
      const draft = bestellMail(r, { ...gespeichert, status: 'erhalten' } as EinkaufAngebot, emailVonLieferant(lieferanten, a.lieferant));
      if (draft.an) { oeffneOutlookEntwurf(draft); onClose(); } else onMail(draft);
    } catch (e) { setFehler((e as Error).message); }
    setBusy(false);
  };
  const pos = [r.menge && `${r.menge}×`, r.form, r.werkstoff, r.format, r.dicke && `${r.dicke} mm`].filter(Boolean).join(' ');
  const STATUS_BTN: [ListenAngebot['status'], string, string][] = [
    ['offen', '⏳ angefragt', 'bg-amber-500'], ['erhalten', '✓ hat angeboten', 'bg-violet-600'], ['absage', '✗ Absage', 'bg-slate-600'],
  ];
  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="angebot-popup">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200">
          <div className="font-semibold text-slate-900">{a.lieferant}</div>
          <div className="text-xs text-slate-500 truncate" title={pos}>{pos || `Bedarf #${r.id}`}{r.referenz ? ` · ${r.referenz}` : ''}</div>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex gap-1.5">
            {STATUS_BTN.map(([v, label, aktiv]) => (
              <button key={v} type="button" data-testid={`angebot-status-${v}`} onClick={() => setStatus(v)}
                      className={`flex-1 rounded-lg px-2 py-1.5 text-sm font-medium border ${status === v ? `${aktiv} text-white border-transparent` : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                {label}
              </button>
            ))}
          </div>
          {status !== 'absage' && (
            <div className="grid grid-cols-3 gap-2">
              <label className="col-span-2 text-xs text-slate-500">Preis (€)
                <input className="input w-full" inputMode="decimal" aria-label="Preis" value={preis} onChange={e => setPreis(e.target.value)} placeholder="0,00" />
              </label>
              <label className="text-xs text-slate-500">je
                <select className="input w-full" aria-label="Einheit" value={einheit} onChange={e => setEinheit(e.target.value)}>
                  <option value="">—</option><option>kg</option><option>Stk</option><option>to</option><option>M</option>
                </select>
              </label>
              <label className="col-span-3 text-xs text-slate-500">Liefertermin
                <input className="input w-full" aria-label="Liefertermin" value={termin} onChange={e => setTermin(e.target.value)} placeholder="z. B. KW28" />
              </label>
            </div>
          )}
          <label className="block text-xs text-slate-500">Notiz
            <input className="input w-full" aria-label="Notiz" value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="optional" />
          </label>
          {fehler && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{fehler}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-2">
          <button type="button" onClick={onClose} className="btn-secondary">Abbrechen</button>
          <div className="flex gap-2">
            <button type="button" data-testid="angebot-speichern" disabled={busy}
                    onClick={async () => { if (await speichern()) onClose(); }}
                    className="btn-secondary">Speichern</button>
            {status !== 'absage' && (
              <button type="button" data-testid="angebot-bestellen" disabled={busy} onClick={() => void bestellen()}
                      className="bg-brand text-white rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
                Hier bestellen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PreisspiegelSektion({ bedarf, lieferanten, onBestellt, onKommentar }: {
  bedarf: Partial<Blechbedarf>; lieferanten: Lieferant[]; onBestellt: (neu: Blechbedarf) => void;
  onKommentar?: (kommentar: string) => void;
}) {
  const bedarfId = bedarf.id as number;
  const angebote = useApi(() => api.einkauf.angebote(bedarfId), [bedarfId]);
  const [neu, setNeu] = useState<Partial<EinkaufAngebot>>({ status: 'offen' });
  const [mail, setMail] = useState<MailDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const lieferantenNamen = lieferanten.map(l => l.name);
  const emailFuer = (name: string): string => {
    const n = name.trim().toLowerCase();
    const hit = lieferanten.find(l =>
      l.name.toLowerCase() === n || n.startsWith(l.name.toLowerCase()) || l.name.toLowerCase().startsWith(n));
    return hit?.email ?? '';
  };
  const rows = angebote.data?.data ?? [];
  const preise = rows.filter(a => a.status !== 'absage' && a.preis != null).map(a => a.preis as number);
  const best = preise.length ? Math.min(...preise) : null;
  // Benchmark: letzter tatsächlich gezahlter Einkaufspreis für dieses Material
  // (automatische Preisabweichungs-Kontrolle wie in SAP).
  const benchmark = useApi(
    () => api.einkauf.preise({ werkstoff: bedarf.werkstoff ?? undefined, dicke: bedarf.dicke ?? undefined }),
    [bedarf.werkstoff, bedarf.dicke], !!bedarf.werkstoff);
  const letzter = benchmark.data?.data?.[0] ?? null;
  const abweichung = (p: number): number | null =>
    letzter && letzter.letzter_preis > 0 ? Math.round(((p - letzter.letzter_preis) / letzter.letzter_preis) * 100) : null;

  // Notiz ohne Lieferant → gehört zum ganzen Bedarf (z. B. Sondersache „Riffelblech“): wird an den Kommentar des Bedarfs angehängt
  const [notizOk, setNotizOk] = useState(false);
  async function bedarfNotizSpeichern() {
    const text = (neu.notiz ?? '').trim();
    if (!text) return;
    setBusy(true);
    try {
      const alt = (bedarf.kommentar ?? '').trim();
      const kommentar = alt ? `${alt}\n${text}` : text;
      await api.blechbedarf.update(bedarfId, { kommentar });
      onKommentar?.(kommentar);
      setNeu(n => ({ ...n, notiz: '' })); setNotizOk(true);
      setTimeout(() => setNotizOk(false), 4000);
    } catch (e) { alert('Notiz konnte nicht gespeichert werden: ' + (e as Error).message); }
    setBusy(false);
  }
  async function hinzufuegen() {
    if (!neu.lieferant?.trim()) { alert('Lieferant fehlt'); return; }
    setBusy(true);
    try { await api.einkauf.angebotAnlegen(bedarfId, neu); setNeu({ status: 'offen' }); angebote.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  }
  async function loeschen(aid: number) {
    if (!confirm('Angebot löschen?')) return;
    try { await api.einkauf.angebotLoeschen(aid); angebote.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
  }
  // Absage versehentlich geklickt → zurück auf "Angebot da" (wenn Preis erfasst) bzw. "angefragt"
  async function absageZurueck(a: EinkaufAngebot) {
    try { await api.einkauf.angebotUpdate(a.id, { status: a.preis != null ? 'erhalten' : 'offen' }); angebote.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
  }
  async function notizSpeichern(a: EinkaufAngebot, text: string) {
    try { await api.einkauf.angebotUpdate(a.id, { notiz: text.trim() || null }); angebote.reload(); }
    catch (e) { alert('Notiz konnte nicht gespeichert werden: ' + (e as Error).message); }
  }
  async function absage(a: EinkaufAngebot) {
    try { await api.einkauf.angebotUpdate(a.id, { status: 'absage' }); angebote.reload(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
  }
  async function bestellen(a: EinkaufAngebot) {
    if (!confirm(`Bei ${a.lieferant} bestellen?\n${a.preis != null ? `${a.preis} €${a.preis_einheit ? '/' + a.preis_einheit : ''} · ` : ''}LT: ${a.liefertermin ?? '—'}`)) return;
    setBusy(true);
    try {
      const r = await api.einkauf.bestellen(bedarfId, a.id);
      onBestellt(r.data);
      // Bestell-Mail vorbereiten: mit bekannter Adresse direkt den logobehafteten
      // Outlook-Entwurf (.eml) erzeugen, sonst Modal zum Nachtragen der Adresse.
      const draft = bestellMail(bedarf, a, emailFuer(a.lieferant));
      if (draft.an) oeffneOutlookEntwurf(draft);
      else setMail(draft);
    }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  }
  const setN = (k: keyof EinkaufAngebot, v: string | number | null) => setNeu(n => ({ ...n, [k]: v }));

  return (
    <section>
      <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
        Preisspiegel <span className="normal-case font-normal">— Angebote vergleichen, per Klick bestellen</span>
      </h3>
      {(bedarf.kommentar ?? '').trim() && (
        <div className="mb-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-1.5 whitespace-pre-wrap" data-testid="bedarf-notiz-anzeige">
          📝 {bedarf.kommentar}
        </div>
      )}
      {notizOk && <div className="mb-2 text-xs text-emerald-700" data-testid="bedarf-notiz-ok">✓ Notiz zum Bedarf gespeichert</div>}
      {letzter && letzter.letzter_preis > 0 && (
        <div className="mb-2 flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
          <span className="text-slate-500">📊 Letzter Einkaufspreis für {bedarf.werkstoff}{bedarf.dicke ? ` · ${bedarf.dicke} mm` : ''}:</span>
          <b className="text-slate-800">{letzter.letzter_preis.toLocaleString('de-DE')} €{letzter.preis_einheit ? `/${letzter.preis_einheit}` : ''}</b>
          {letzter.lieferant && <span className="text-slate-500">bei {letzter.lieferant}</span>}
          {letzter.letztes_datum && <span className="text-slate-400">({fmtDatum(letzter.letztes_datum)})</span>}
          <span className="text-slate-400">· Vergleichswert für neue Angebote</span>
        </div>
      )}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-[13px] min-w-[560px]">
          <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
            <tr>
              {['Lieferant','Status','Preis','Einheit','Liefertermin','Notiz',''].map((h, i) => (
                <th key={i} className="text-left px-2 py-1.5 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(a => (
              <tr key={a.id} className={a.status === 'absage' ? '[&>td:not(:last-child)]:opacity-50' : ''}>
                <td className="px-2 py-1.5 font-medium">{a.lieferant}</td>
                <td className="px-2 py-1.5">
                  {a.status === 'absage' ? <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-xs">Absage</span>
                   : a.status === 'erhalten' ? <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded text-xs">Angebot da</span>
                   : <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-xs">angefragt</span>}
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                  {a.preis != null ? a.preis.toLocaleString('de-DE') + ' €' : '—'}
                  {a.preis != null && a.preis === best && a.status !== 'absage' && (
                    <span className="ml-1.5 bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">günstigst</span>
                  )}
                  {a.preis != null && a.status !== 'absage' && abweichung(a.preis) != null && Math.abs(abweichung(a.preis) as number) >= 10 && (
                    <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${(abweichung(a.preis) as number) > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
                          title={`Letzter Einkaufspreis: ${letzter?.letzter_preis?.toLocaleString('de-DE')} €${letzter?.preis_einheit ? '/' + letzter.preis_einheit : ''}`}>
                      {(abweichung(a.preis) as number) > 0 ? '+' : ''}{abweichung(a.preis)}%
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5">{a.preis_einheit ?? '—'}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">{fmtTermin(a.liefertermin)}</td>
                <td className="px-2 py-1.5 min-w-[140px]">
                  {/* Notiz je Angebot direkt in der Zeile bearbeitbar — speichert beim Verlassen des Feldes / mit Enter */}
                  <input key={`${a.id}-${a.notiz ?? ''}`} className="input w-full py-1 text-[13px]" placeholder="Notiz …" aria-label={`Notiz ${a.lieferant}`}
                         defaultValue={a.notiz ?? ''} title={a.notiz ?? ''}
                         onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
                         onBlur={e => { if (e.target.value.trim() !== (a.notiz ?? '')) void notizSpeichern(a, e.target.value); }} />
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-right">
                  {a.status !== 'absage' && (
                    <button onClick={() => bestellen(a)} disabled={busy}
                            className="bg-brand text-white rounded px-2 py-0.5 text-xs font-medium hover:opacity-90 disabled:opacity-50 mr-1">
                      Bestellen
                    </button>
                  )}
                  {a.status !== 'absage' && (
                    <button onClick={() => absage(a)} title="Als Absage markieren"
                            className="text-slate-400 hover:text-slate-600 text-xs mr-1">Absage</button>
                  )}
                  {a.status === 'absage' && (
                    <button onClick={() => absageZurueck(a)} title="Absage rückgängig machen" data-testid="absage-rueckgaengig"
                            className="text-brand hover:underline text-xs mr-1 font-medium">↩ Rückgängig</button>
                  )}
                  <button onClick={() => loeschen(a.id)} title="Löschen"
                          className="text-slate-400 hover:text-red-600 text-xs">×</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-3 text-center text-slate-400">
                Noch keine Angebote — unten das erste erfassen.
              </td></tr>
            )}
            {letzter && (
              <tr><td colSpan={7} className="px-3 py-1.5 text-xs text-slate-500 bg-slate-50/70">
                📈 Letzter Einkaufspreis {letzter.werkstoff}{letzter.dicke ? ` · ${letzter.dicke}mm` : ''}:{' '}
                <b>{letzter.letzter_preis.toLocaleString('de-DE')} €{letzter.preis_einheit ? `/${letzter.preis_einheit}` : ''}</b>
                {letzter.lieferant ? ` · ${letzter.lieferant}` : ''}{letzter.letztes_datum ? ` · ${fmtDatum(letzter.letztes_datum)}` : ''}
                {' '}— Angebote mit ±10% Abweichung werden markiert
              </td></tr>
            )}
          </tbody>
        </table>
        </div>

        {/* Erfassung: neues Angebot (Fußzeile der Karte) */}
        <div className="border-t border-slate-200 bg-slate-50/70 px-3 py-2.5"
             onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void hinzufuegen(); } }}>
          <div className="flex flex-wrap items-end gap-x-2 gap-y-2">
            <label className="flex-[2] min-w-[150px]">
              <span className="block text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Lieferant</span>
              <AutoVervollstaendigung value={neu.lieferant ?? ''} onChange={v => setN('lieferant', v)}
                                      optionen={lieferantenNamen} placeholder="Name …" />
            </label>
            <label className="w-[118px]">
              <span className="block text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Status</span>
              <select className="input w-full" value={neu.status ?? 'offen'}
                      onChange={e => setN('status', e.target.value)}>
                <option value="offen">angefragt</option>
                <option value="erhalten">Angebot da</option>
                <option value="absage">Absage</option>
              </select>
            </label>
            <label className="w-[104px]">
              <span className="block text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Preis</span>
              <div className="relative">
                <input type="number" step="0.01" className="input w-full pr-6" placeholder="0,00"
                       value={neu.preis ?? ''} onChange={e => setN('preis', e.target.value === '' ? null : Number(e.target.value))} />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">€</span>
              </div>
            </label>
            <label className="w-[72px]">
              <span className="block text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">je</span>
              <select className="input w-full" value={neu.preis_einheit ?? ''}
                      onChange={e => setN('preis_einheit', e.target.value)}>
                <option value="">—</option><option>kg</option><option>Stk</option><option>to</option><option>M</option>
              </select>
            </label>
            <label className="w-[130px]">
              <span className="block text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Liefertermin</span>
              <input className="input w-full" placeholder="z. B. KW28"
                     value={neu.liefertermin ?? ''} onChange={e => setN('liefertermin', e.target.value)} />
            </label>
            <label className="flex-1 min-w-[110px]">
              <span className="block text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-0.5">Notiz</span>
              <input className="input w-full" placeholder="optional"
                     value={neu.notiz ?? ''} onChange={e => setN('notiz', e.target.value)} />
            </label>
            {neu.lieferant?.trim() ? (
              <button onClick={hinzufuegen} disabled={busy}
                      className="bg-brand text-white rounded-lg px-3.5 h-[38px] text-sm font-medium whitespace-nowrap hover:opacity-90 disabled:opacity-40 shrink-0">
                + Angebot
              </button>
            ) : (
              <button onClick={() => void bedarfNotizSpeichern()} disabled={busy || !(neu.notiz ?? '').trim()} data-testid="bedarf-notiz-btn"
                      title="Ohne Lieferant: Notiz zum ganzen Bedarf (z. B. Sondersache) — wird im Kommentar des Bedarfs gespeichert"
                      className="bg-slate-700 text-white rounded-lg px-3.5 h-[38px] text-sm font-medium whitespace-nowrap hover:opacity-90 disabled:opacity-40 shrink-0">
                + Notiz
              </button>
            )}
          </div>
        </div>
      </div>
      {mail && <MailModal draft={mail} onChange={setMail} onClose={() => setMail(null)} />}
    </section>
  );
}

// 📎 Dokumente (PDF) je Bedarf — Bestell-PDF, AB, Zeugnis … (R2 über die API)
function fmtGroesse(b: number): string {
  return b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
}

function BedarfDateien({ id }: { id: number }) {
  const [dateien, setDateien] = useState<BedarfDatei[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadTyp, setUploadTyp] = useState<string>('bestellung');

  const laden = () => api.einkauf.dateien(id).then(r => setDateien(r.data)).catch(() => setDateien([]));
  useEffect(() => { setDateien(null); void laden(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const hochladen = async (files: FileList | null, typ: string) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        // Zeugnisse zusätzlich mit Namenspräfix (Rückwärtskompatibilität mit alten Uploads)
        const datei = typ === 'zeugnis' && !f.name.startsWith('Zeugnis_')
          ? new File([f], `Zeugnis_${f.name}`, { type: f.type })
          : f;
        await api.einkauf.dateiHochladen(id, datei, typ);
      }
      await laden();
    } catch (e) {
      const msg = e instanceof ApiError && (e.body as { error?: string } | null)?.error;
      alert(`Upload fehlgeschlagen: ${msg || String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  // Typ aus Metadaten, ersatzweise aus dem Zeugnis-Namenspräfix (Altbestand)
  const dateiTyp = (d: BedarfDatei): string =>
    d.typ ?? (d.name.startsWith('Zeugnis_') ? 'zeugnis' : 'sonstiges');

  const loeschen = async (name: string) => {
    if (!confirm(`Datei "${name}" löschen?`)) return;
    setBusy(true);
    try { await api.einkauf.dateiLoeschen(id, name); await laden(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-1.5">
      {dateien === null && <div className="text-sm text-slate-400">Lade …</div>}
      {dateien?.map(d => {
        const t = dateiTyp(d);
        const meta = DOK_TYP[t] ?? DOK_TYP.sonstiges!;
        return (
        <div key={d.name} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1">
          <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${meta.cls}`}>{meta.icon} {meta.label}</span>
          <a href={api.einkauf.dateiUrl(id, d.name)} target="_blank" rel="noreferrer"
             className="text-brand hover:underline truncate" title={d.name}>{d.name}</a>
          <span className="text-xs text-slate-400 shrink-0 ml-auto">
            {fmtGroesse(d.groesse)}{d.von ? ` · ${d.von}` : ''} · {fmtDatum(d.hochgeladen.slice(0, 10))}
          </span>
          <button type="button" onClick={() => loeschen(d.name)} disabled={busy}
                  className="text-slate-400 hover:text-red-600 shrink-0 disabled:opacity-50" title="Löschen">×</button>
        </div>
      );})}
      {dateien?.length === 0 && <div className="text-sm text-slate-400">Noch keine Dokumente.</div>}
      <div className="flex items-center gap-2 flex-wrap">
        <select className="input text-sm py-1" value={uploadTyp} onChange={e => setUploadTyp(e.target.value)} title="Dokument-Art">
          {Object.entries(DOK_TYP).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
        <label className={`inline-flex items-center gap-1.5 text-sm text-brand hover:underline cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
          <input type="file" accept="application/pdf,.pdf" multiple className="hidden"
                 onChange={e => { void hochladen(e.target.files, uploadTyp); e.target.value = ''; }} />
          {busy ? 'Lädt hoch …' : '＋ PDF hochladen'}
        </label>
      </div>
    </div>
  );
}

// Rechnungsprüfung (D4): Rechnungsnr + Betrag erfassen, mit der Bestellung
// abgleichen, als geprüft markieren → fällt aus den offenen Rechnungen.
function RechnungSektion({ modal, set }: {
  modal: Partial<Blechbedarf>;
  set: (k: keyof Blechbedarf, v: string | number | null) => void;
}) {
  const heute = () => new Date().toISOString().slice(0, 10);
  // erwarteter Betrag = Preis × Menge (nur wenn Menge eine reine Zahl ist)
  const mengeZahl = Number(String(modal.menge ?? '').replace(',', '.'));
  const erwartet = modal.preis != null && !isNaN(mengeZahl) && mengeZahl > 0
    ? modal.preis * mengeZahl : null;
  const betrag = modal.rechnungsbetrag ?? null;
  const abw = erwartet && betrag != null && erwartet > 0
    ? Math.round(((betrag - erwartet) / erwartet) * 100) : null;
  const geprueft = !!modal.re_geprueft;
  return (
    <KlappSektion key={`s45-inner`} titel="Rechnungsprüfung"
      offenDefault={!geprueft}
      zusammenfassung={geprueft
        ? `✓ geprüft${modal.rechnungsnr ? ` · RE ${modal.rechnungsnr}` : ''}${modal.re_datum ? ` · ${fmtDatum(modal.re_datum)}` : ''}`
        : modal.rechnungsnr ? `RE ${modal.rechnungsnr} · noch nicht geprüft` : 'keine Rechnung erfasst'}
      warnung={abw != null && Math.abs(abw) >= 5 ? `Rechnung weicht ${abw > 0 ? '+' : ''}${abw}% vom Bestellwert ab` : null}>
      <F label="Rechnungs-Nr"><input className="input w-full" value={modal.rechnungsnr ?? ''} onChange={e => set('rechnungsnr', e.target.value)} placeholder="z. B. 8046099125" /></F>
      <F label="Rechnungsbetrag (€ netto)"><input type="number" step="0.01" className="input w-full" value={modal.rechnungsbetrag ?? ''} onChange={e => set('rechnungsbetrag', e.target.value === '' ? null : Number(e.target.value))} /></F>
      <F label="Erwartet (Preis × Menge)">
        <div className="px-2.5 py-1.5 text-sm">
          {erwartet != null ? `${erwartet.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : <span className="text-slate-400">— (Menge/Preis unklar)</span>}
          {abw != null && (
            <span className={`ml-2 text-[11px] font-bold px-1.5 py-0.5 rounded ${Math.abs(abw) < 5 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
              {Math.abs(abw) < 5 ? '✓ stimmt' : `${abw > 0 ? '+' : ''}${abw}%`}
            </span>
          )}
        </div>
      </F>
      <F label="">
        <label className="flex items-center gap-1.5 text-sm pt-2">
          <input type="checkbox" checked={geprueft}
            onChange={e => {
              set('re_geprueft', e.target.checked ? 1 : 0);
              if (e.target.checked && !(modal.re_datum ?? '').trim()) set('re_datum', heute());
            }} />
          Rechnung geprüft &amp; stimmt {geprueft && modal.re_datum && <span className="text-slate-400">({fmtDatum(modal.re_datum)})</span>}
        </label>
      </F>
    </KlappSektion>
  );
}

// Dokument-Arten (Ablage nach Kategorie): Bestell-PDF, AB, Angebot, Rechnung, Zeugnis, …
const DOK_TYP: Record<string, { label: string; icon: string; cls: string }> = {
  bestellung: { label: 'Bestellung', icon: '📦', cls: 'bg-sky-100 text-sky-800' },
  ab:         { label: 'AB',         icon: '✅', cls: 'bg-emerald-100 text-emerald-800' },
  angebot:    { label: 'Angebot',    icon: '💶', cls: 'bg-violet-100 text-violet-800' },
  rechnung:   { label: 'Rechnung',   icon: '🧾', cls: 'bg-amber-100 text-amber-800' },
  zeugnis:    { label: 'Zeugnis',    icon: '📜', cls: 'bg-teal-100 text-teal-800' },
  sonstiges:  { label: 'Sonstiges',  icon: '📄', cls: 'bg-slate-100 text-slate-600' },
};

function StatusBadgeBB({ r }: { r: Blechbedarf }) {
  if (r.fertig)   return <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap">Angekommen</span>;
  if (r.bestellt) return <span className="bg-sky-100 text-sky-800 px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap">Bestellt{r.ab_bestaetigt ? ' · AB ✓' : ''}</span>;
  if ((r.angebote_da ?? 0) > 0) return <span className="bg-violet-100 text-violet-800 px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap">Angebot da</span>;
  if ((r.lieferanten_angefragt ?? '').trim() || (r.anfrage_datum ?? '').trim())
    return <span className="bg-teal-100 text-teal-800 px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap">Angefragt</span>;
  return <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap">Offen</span>;
}

// Zusammenklappbare Prozess-Sektion: die aktive Stufe ist offen, erledigte
// Stufen zeigen nur eine Zusammenfassungszeile (Klick öffnet). So sieht man
// im Modal nur, was gerade dran ist — statt aller fünf Phasen auf einmal.
// ton: 'juergen' = heller Grau-Ton (Bedarf, Wareneingang — Jürgens Teil),
//      'einkauf' = dunklerer Grau-Ton (Anfrage, Bestellung, Dokumente).
function KlappSektion({ titel, offenDefault, zusammenfassung, warnung, ton, children }: {
  titel: string; offenDefault: boolean; zusammenfassung?: string;
  warnung?: string | null; ton?: 'juergen' | 'einkauf'; children: React.ReactNode;
}) {
  const [manuell, setManuell] = useState<boolean | null>(null);
  const offen = manuell ?? offenDefault;
  const rahmen = ton === 'einkauf' ? 'border-slate-300 border-l-4 border-l-slate-500'
              : ton === 'juergen' ? 'border-slate-200 border-l-4 border-l-slate-300'
              : 'border-slate-200';
  const kopfBg = ton === 'einkauf' ? (offen ? 'bg-slate-200 border-b border-slate-300' : 'bg-slate-100 hover:bg-slate-200')
              : ton === 'juergen' ? (offen ? 'bg-slate-100 border-b border-slate-200' : 'bg-slate-50 hover:bg-slate-100')
              : (offen ? 'bg-slate-50 border-b border-slate-200' : 'bg-white hover:bg-slate-50');
  return (
    <section className={`border rounded-lg overflow-hidden ${rahmen}`}>
      <button type="button" onClick={() => setManuell(o => !(o ?? offenDefault))}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left ${kopfBg}`}>
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{titel}</span>
        {!offen && zusammenfassung && <span className="text-sm text-slate-600 truncate">{zusammenfassung}</span>}
        {warnung && <span className="text-xs bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 whitespace-nowrap">⚠ {warnung}</span>}
        <span className="ml-auto text-slate-400 text-sm">{offen ? '▾' : '▸'}</span>
      </button>
      {offen && <div className={`p-3 ${ton === 'einkauf' ? 'bg-slate-50' : ''}`}><div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div></div>}
    </section>
  );
}

// Aktivitäts-Verlauf aus blechbedarf_history — wer hat wann was geändert.
const FELD_LABEL: Record<string, string> = {
  lieferanten_angefragt: 'Lieferanten angefragt', anfrage_datum: 'Anfrage-Datum', anfrage_notiz: 'Anfrage-Notiz',
  bestell_datum: 'Bestell-Datum', besteller: 'Besteller', lieferant: 'Lieferant', liefertermin: 'Liefertermin',
  bestellt: 'bestellt', preis: 'Preis', preis_einheit: 'Preis-Einheit', wareneingang: 'Wareneingang',
  menge_angekommen: 'Menge angekommen', fertig: 'fertig', bestellnr: 'AB-/Bestell-Nr', ab_bestaetigt: 'AB bestätigt',
  kommentar: 'Kommentar', artikel_id: 'Artikel', lieferanten_artikelnr: 'Lieferanten-Art.-Nr.', termin: 'Termin', menge: 'Menge', werkstoff: 'Werkstoff', format: 'Format',
  dicke: 'Dicke', form: 'Form', referenz: 'Referenz', priority: 'Priorität', zeugnis: 'Zeugnis', datum: 'Datum', sb: 'SB',
};
function Verlauf({ id }: { id: number }) {
  const hist = useApi(() => api.einkauf.history(id), [id]);
  const rows = hist.data?.data ?? [];
  const zeile = (h: BedarfHistorie): string => {
    if (h.action === 'INSERT') return 'angelegt';
    if (h.action === 'DELETE') {
      try { const n = JSON.parse(h.new_data ?? '{}') as { storno_grund?: string }; return n.storno_grund ? `storniert: ${n.storno_grund}` : 'storniert'; }
      catch { return 'storniert'; }
    }
    if (h.action === 'RESTORE') return 'aus dem Papierkorb wiederhergestellt';
    try {
      const neu = JSON.parse(h.new_data ?? '{}') as Record<string, unknown>;
      const teile = Object.entries(neu)
        .filter(([k]) => k !== 'id' && k !== 'updated_at' && k !== 'updated_by_id')
        .map(([k, v]) => {
          if (k === 'bestellt' && v) return 'bestellt ✓';
          if (k === 'fertig' && v) return 'Ware angekommen ✓';
          const wert = v === null || v === '' ? '—' : String(v);
          return `${FELD_LABEL[k] ?? k}: ${wert.length > 40 ? wert.slice(0, 40) + '…' : wert}`;
        });
      if (!teile.length) return 'geändert';
      return teile.slice(0, 3).join(' · ') + (teile.length > 3 ? ` · +${teile.length - 3} weitere` : '');
    } catch { return 'geändert'; }
  };
  return (
    <div className="sm:col-span-2 space-y-1">
      {hist.loading && <div className="text-sm text-slate-400">Lädt …</div>}
      {rows.map(h => (
        <div key={h.id} className="flex gap-2 text-[13px] items-baseline">
          <span className="text-slate-400 whitespace-nowrap tabular-nums">{h.changed_at.slice(0, 16).replace('T', ' ')}</span>
          <span className="text-slate-500 whitespace-nowrap">{h.user_name ?? '—'}</span>
          <span className="text-slate-700">{zeile(h)}</span>
        </div>
      ))}
      {!hist.loading && rows.length === 0 && <div className="text-sm text-slate-400">Noch keine Einträge.</div>}
    </div>
  );
}


// Artikel aus dem Stamm wählen oder direkt neu anlegen (jeder darf).
function ArtikelWahl({ kat, aktuell, lieferantenListe, onWahl }: {
  kat: 'blech' | 'profilrohr' | 'sonstiges'; aktuell: Partial<Blechbedarf>; lieferantenListe: string[]; onWahl: (a: Artikel) => void;
}) {
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [offen, setOffen] = useState(false);
  const [neu, setNeu] = useState<Partial<Artikel> | null>(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [info, setInfo] = useState('');
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const res = useApi(() => api.artikel.list({ q: dq || undefined, kat }), [dq, kat], offen);
  const treffer: Artikel[] = res.data?.data ?? [];
  const waehle = (a: Artikel) => { onWahl(a); setOffen(false); setQ(''); setNeu(null); setInfo(`Artikel „${a.bezeichnung}" übernommen`); };
  const oeffneNeu = () => setNeu({
    form: aktuell.form ?? '', werkstoff: aktuell.werkstoff ?? '', format: aktuell.format ?? '', dicke: aktuell.dicke ?? '',
    lieferant: aktuell.lieferant ?? '', lieferanten_artikelnr: aktuell.lieferanten_artikelnr ?? '', bezeichnung: '',
  });
  async function anlegen() {
    if (!neu) return;
    if (!(neu.form ?? '').trim() && !(neu.werkstoff ?? '').trim() && !(neu.bezeichnung ?? '').trim()) {
      setFehler('Bitte mindestens Form, Werkstoff oder Bezeichnung angeben.'); return;
    }
    setBusy(true); setFehler('');
    try {
      const r = await api.artikel.create({ ...neu, kategorie: kat });
      waehle(r.data);
      if (r.existing) setInfo(`Artikel gab es schon — „${r.data.bezeichnung}" übernommen`);
    } catch (e) { setFehler((e as Error).message); }
    setBusy(false);
  }
  const nf = (k: keyof Artikel, label: string, ph?: string) => (
    <label className="block text-xs text-slate-500">{label}
      <input className="input w-full mt-0.5" placeholder={ph} value={(neu?.[k] as string | null) ?? ''}
             onChange={e => setNeu(n => ({ ...n, [k]: e.target.value }))} />
    </label>
  );
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3" data-testid="artikel-wahl">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-medium text-sky-900">Artikel</span>
        <input className="input flex-1 min-w-[200px]" placeholder="🔍 Artikel suchen (Werkstoff, Format, Lieferant, Art.-Nr.) …"
               value={q} onFocus={() => setOffen(true)} onChange={e => { setQ(e.target.value); setOffen(true); }} />
        <button type="button" onClick={oeffneNeu} className="border border-sky-600 text-sky-800 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-sky-100">+ Neuer Artikel</button>
        {aktuell.artikel_id ? <span className="text-xs text-emerald-700">✓ Artikel #{aktuell.artikel_id}</span> : null}
      </div>
      {info && <div className="mt-1 text-xs text-emerald-700">{info}</div>}
      {offen && !neu && (
        <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
          {res.loading && <div className="px-3 py-2 text-sm text-slate-400">Lädt …</div>}
          {!res.loading && treffer.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">Kein Artikel gefunden — mit „+ Neuer Artikel“ anlegen.</div>}
          {treffer.map(a => (
            <button type="button" key={a.id} onClick={() => waehle(a)} className="w-full text-left px-3 py-1.5 hover:bg-sky-50 text-[13px]">
              <span className="font-medium text-slate-800">{a.bezeichnung}</span>
              {a.lieferant ? <span className="ml-2 text-slate-500">· {a.lieferant}</span> : null}
              {a.lieferanten_artikelnr ? <span className="ml-2 text-slate-500">· Art.-Nr. {a.lieferanten_artikelnr}</span> : null}
            </button>
          ))}
        </div>
      )}
      {neu && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {nf('form', 'Form')}{nf('werkstoff', 'Werkstoff')}{nf('format', 'Format')}{nf('dicke', 'Dicke (mm)')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {nf('bezeichnung', 'Bezeichnung (optional)', 'wird sonst automatisch gebildet')}
            <label className="block text-xs text-slate-500">Lieferant (optional)
              <input className="input w-full mt-0.5" list="artikel-lieferanten" value={neu.lieferant ?? ''}
                     onChange={e => setNeu(n => ({ ...n, lieferant: e.target.value }))} />
              <datalist id="artikel-lieferanten">{lieferantenListe.map(l => <option key={l} value={l} />)}</datalist>
            </label>
            {nf('lieferanten_artikelnr', 'Lieferanten-Art.-Nr. (optional)')}
          </div>
          {fehler && <div className="text-sm text-red-600">{fehler}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setNeu(null); setFehler(''); }} className="btn-secondary">Abbrechen</button>
            <button type="button" onClick={anlegen} disabled={busy}
                    className="bg-brand text-white rounded-lg px-4 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50">
              {busy ? 'Legt an …' : 'Artikel anlegen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sammelbestellung je DV: offene Positionen ankreuzen → eine Bestellung je
// Lieferant. Setzt Lieferant/Bestellt/Bestell-Datum/Besteller/Bestell-Nr auf
// allen angekreuzten Positionen und erzeugt je Lieferant einen Bestellzettel
// (Mail-Entwurf / PDF).
// ---------------------------------------------------------------------------
type DvZeile = BedarfDvUebersicht['data'][number];

export function lieferantVonName(lieferanten: Lieferant[], name: string): Lieferant | undefined {
  const n = name.trim().toLowerCase();
  if (!n) return undefined;
  return lieferanten.find(l =>
    l.name.toLowerCase() === n || n.startsWith(l.name.toLowerCase()) || l.name.toLowerCase().startsWith(n));
}
export function emailVonLieferant(lieferanten: Lieferant[], name: string): string {
  return lieferantVonName(lieferanten, name)?.email ?? '';
}

// Positionen der Bestellung als PDF-Daten (Briefkopf wie im Kalkulation-Angebot)
export function sammelPdfDaten(lieferant: Lieferant | undefined, name: string, bestellnr: string, dvQ: string, pos: Partial<Blechbedarf>[]): BestellPdfDaten {
  return {
    lieferant: lieferant?.name ?? name,
    ansprechpartner: lieferant?.ansprechpartner,
    bestellnr: bestellnr || dvQ,
    materialzeugnis: true,
    positionen: pos.map((r, i) => {
      const mat = [r.form, r.werkstoff, r.format, r.dicke ? `${r.dicke} mm` : null].filter(Boolean).join(' · ');
      return {
        nr: (i + 1) * 10,
        artikelnr: r.lieferanten_artikelnr,
        bezeichnung: mat || '—',
        menge: String(r.menge ?? '—'),
        einheit: 'Stk',
        liefertermin: r.liefertermin,
      };
    }),
  };
}

function sammelMail(_lieferant: string, bestellnr: string, dvQ: string, pos: Partial<Blechbedarf>[], email: string): MailDraft {
  const nr = bestellnr || dvQ;
  return {
    an: email,
    betreff: `Bestellung ${nr} – ${pos.length} Position${pos.length === 1 ? '' : 'en'}`,
    text: `Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie unsere Bestellung Nr. ${nr} (${pos.length} Position${pos.length === 1 ? '' : 'en'}) als PDF.\n\n` +
      'Bitte senden Sie uns die Auftragsbestätigung mit verbindlichem Liefertermin' +
      ' und legen Sie der Lieferung das Materialzeugnis bei.' +
      '\n\nVielen Dank.\n\n' + MAIL_SIGNATUR,
  };
}

export function SammelBestellModal({ dvQ, rows, lieferanten, onClose }: {
  dvQ: string; rows: DvZeile[]; lieferanten: Lieferant[]; onClose: () => void;
}) {
  const offene = rows.filter(r => !r.bestellt && !r.fertig);
  const [wahl, setWahl] = useState<Record<number, boolean>>(
    () => Object.fromEntries(offene.map(r => [r.id, !!(r.lieferant ?? '').trim()])));
  const [lief, setLief] = useState<Record<number, string>>(
    () => Object.fromEntries(offene.map(r => [r.id, r.lieferant ?? ''])));
  const [kopf, setKopf] = useState<Record<string, { bestellnr?: string; liefertermin?: string }>>({});
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [ergebnis, setErgebnis] = useState<SammelErgebnis | null>(null);
  const [mail, setMail] = useState<MailDraft | null>(null);
  const [mailPdf, setMailPdf] = useState<BestellPdfDaten | null>(null);
  useEffect(() => { bestellPdfVorladen(); }, []);

  const gewaehlt = offene.filter(r => wahl[r.id]);
  const ohneLieferant = gewaehlt.filter(r => !(lief[r.id] ?? '').trim());
  // Gruppen je Lieferant (Schlüssel = getrimmter Name, Groß/Klein egal)
  const gruppen = new Map<string, { name: string; rows: DvZeile[] }>();
  for (const r of gewaehlt) {
    const name = (lief[r.id] ?? '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const g = gruppen.get(key) ?? { name, rows: [] };
    g.rows.push(r); gruppen.set(key, g);
  }
  const gruppenListe = [...gruppen.entries()];
  const gemeinsameDv = (g: DvZeile[]) => {
    const dvs = new Set(g.map(r => (r.referenz ?? '').trim()));
    return dvs.size === 1 ? ([...dvs][0] ?? '') : '';
  };
  const mehrereDv = new Set(gewaehlt.map(r => (r.referenz ?? '').trim())).size > 1;
  const lieferantenNamen = lieferanten.map(l => l.name);
  const setK = (key: string, f: 'bestellnr' | 'liefertermin', v: string) =>
    setKopf(k => ({ ...k, [key]: { ...k[key], [f]: v } }));

  async function bestellen() {
    if (gewaehlt.length === 0 || ohneLieferant.length > 0) return;
    const txt = gruppenListe.map(([, g]) => `${g.name}: ${g.rows.length} Position${g.rows.length === 1 ? '' : 'en'}`).join('\n');
    if (!confirm(`Jetzt bestellen?\n\n${txt}\n\nDie Positionen werden als „bestellt“ markiert.`)) return;
    setBusy(true); setFehler('');
    try {
      const r = await api.blechbedarf.sammelbestellung(gruppenListe.map(([key, g]) => ({
        lieferant: g.name,
        ids: g.rows.map(x => x.id),
        bestellnr: (kopf[key]?.bestellnr ?? gemeinsameDv(g.rows)).trim() || undefined,
        liefertermin: kopf[key]?.liefertermin?.trim() || undefined,
      })));
      setErgebnis(r);
    } catch (e) { setFehler((e as Error).message); }
    setBusy(false);
  }

  return (
    <>
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl my-8" data-testid="sammel-modal" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">📋 Bestellung anlegen · DV „{dvQ}“</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        {ergebnis ? (
          <div className="p-6 space-y-3" data-testid="sammel-ergebnis">
            <div className="text-emerald-800 font-semibold">
              ✅ {ergebnis.anzahl} Position{ergebnis.anzahl === 1 ? '' : 'en'} bestellt ({ergebnis.data.length} Lieferant{ergebnis.data.length === 1 ? '' : 'en'})
            </div>
            {ergebnis.nicht_bestellt.length > 0 && (
              <div className="text-sm text-red-600">Nicht bestellt (zwischenzeitlich geändert): {ergebnis.nicht_bestellt.join(', ')}</div>
            )}
            <p className="text-sm text-slate-600">Bestellt-Felder (Lieferant, Datum, Besteller, Bestell-Nr) sind gesetzt. Bestellzettel je Lieferant:</p>
            {ergebnis.data.map(g => {
              const pos = g.positionen as Partial<Blechbedarf>[];
              const nr = g.bestellnr ?? (pos[0]?.bestellnr ?? '');
              return (
                <div key={g.lieferant} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-4 py-2">
                  <div className="text-sm"><b>{g.lieferant}</b> · {pos.length} Position{pos.length === 1 ? '' : 'en'} · Best.-Nr {nr || '—'}</div>
                  <button className="bg-brand text-white rounded-lg px-4 py-1.5 text-sm font-medium hover:opacity-90"
                          onClick={() => {
                            setMailPdf(sammelPdfDaten(lieferantVonName(lieferanten, g.lieferant), g.lieferant, nr, dvQ, pos));
                            setMail(sammelMail(g.lieferant, nr, dvQ, pos, emailVonLieferant(lieferanten, g.lieferant)));
                          }}>
                    ✉ Bestellzettel / Mail
                  </button>
                </div>
              );
            })}
            <div className="flex justify-end pt-2"><button className="btn-secondary" onClick={onClose}>Schließen</button></div>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600">
                Offene Positionen ankreuzen, Lieferant prüfen — je Lieferant wird <b>eine</b> Bestellung ausgelöst.
                Die Bestell-Nr ist standardmäßig die DV-Nr.
              </p>
              <datalist id="sammel-lieferanten">{lieferantenNamen.map(n => <option key={n} value={n} />)}</datalist>
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-100 text-xs text-slate-600 uppercase">
                    <tr>{['', 'DV', 'Menge', 'Form', 'Werkstoff', 'Format', 'Dicke', 'Lief.-Art.-Nr.', 'Lieferant', 'Stand'].map(h => (
                      <th key={h} className="text-left px-2.5 py-1.5 font-medium whitespace-nowrap">{h}</th>))}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {offene.map(r => (
                      <tr key={r.id} className={wahl[r.id] ? 'bg-emerald-50/50' : ''}>
                        <td className="px-2.5 py-1.5">
                          <input type="checkbox" aria-label={`Position ${r.id} wählen`} checked={!!wahl[r.id]}
                                 onChange={e => setWahl(w => ({ ...w, [r.id]: e.target.checked }))} />
                        </td>
                        <td className="px-2.5 py-1.5 font-semibold whitespace-nowrap">{r.referenz}</td>
                        <td className="px-2.5 py-1.5">{r.menge ?? '—'}</td>
                        <td className="px-2.5 py-1.5">{r.form ?? '—'}</td>
                        <td className="px-2.5 py-1.5">{r.werkstoff ?? '—'}</td>
                        <td className="px-2.5 py-1.5">{r.format ?? '—'}</td>
                        <td className="px-2.5 py-1.5">{r.dicke ?? '—'}</td>
                        <td className="px-2.5 py-1.5">{r.lieferanten_artikelnr ?? '—'}</td>
                        <td className="px-2.5 py-1.5">
                          <input className="input w-44" list="sammel-lieferanten" aria-label={`Lieferant Position ${r.id}`}
                                 value={lief[r.id] ?? ''} placeholder="Lieferant …"
                                 onChange={e => { const v = e.target.value; const warLeer = !(lief[r.id] ?? '').trim(); setLief(l => ({ ...l, [r.id]: v })); if (v.trim() && warLeer) setWahl(w => ({ ...w, [r.id]: true })); }} />
                        </td>
                        <td className="px-2.5 py-1.5 whitespace-nowrap">
                          {(r.lieferanten_angefragt || r.anfrage_datum) ? '✉ angefragt' : '○ offen'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {mehrereDv && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Achtung: Die Auswahl enthält verschiedene DV-Nummern. Ohne eigene Bestell-Nr bekommt jede Position ihre eigene DV-Nr.
                </div>
              )}
              {ohneLieferant.length > 0 && (
                <div className="text-xs text-red-600" data-testid="sammel-ohne-lieferant">
                  {ohneLieferant.length} gewählte Position{ohneLieferant.length === 1 ? ' hat' : 'en haben'} noch keinen Lieferant.
                </div>
              )}

              {gruppenListe.length > 0 && (
                <div className="space-y-2" data-testid="sammel-gruppen">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Bestellungen ({gruppenListe.length})</div>
                  {gruppenListe.map(([key, g]) => (
                    <div key={key} className="flex flex-wrap items-center gap-3 border border-slate-200 rounded-lg px-4 py-2">
                      <div className="text-sm font-semibold min-w-[12rem]">{g.name} <span className="font-normal text-slate-500">· {g.rows.length} Position{g.rows.length === 1 ? '' : 'en'}</span></div>
                      <label className="text-xs text-slate-500">Bestell-Nr
                        <input className="input ml-2 w-40" aria-label={`Bestell-Nr ${g.name}`}
                               value={kopf[key]?.bestellnr ?? gemeinsameDv(g.rows)}
                               onChange={e => setK(key, 'bestellnr', e.target.value)} />
                      </label>
                      <label className="text-xs text-slate-500">Liefertermin (optional)
                        <input className="input ml-2 w-36" aria-label={`Liefertermin ${g.name}`} placeholder="z. B. KW 44"
                               value={kopf[key]?.liefertermin ?? ''}
                               onChange={e => setK(key, 'liefertermin', e.target.value)} />
                      </label>
                    </div>
                  ))}
                </div>
              )}
              {fehler && <div className="text-sm text-red-600">{fehler}</div>}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
              <button onClick={bestellen} data-testid="sammel-bestellen"
                      disabled={busy || gewaehlt.length === 0 || ohneLieferant.length > 0}
                      className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {busy ? 'Legt an …' : `Bestellung anlegen (${gewaehlt.length} Position${gewaehlt.length === 1 ? '' : 'en'}, ${gruppenListe.length} Lieferant${gruppenListe.length === 1 ? '' : 'en'})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
    {mail && <MailModal draft={mail} onChange={setMail} onClose={() => setMail(null)} titel="✉ Bestellzettel an Lieferant"
                        pdf={mailPdf ? () => bestellPdfErzeugen({ ...mailPdf, bestellnr: mailPdf.bestellnr }) : undefined} />}
    </>
  );
}

function Papierkorb({ kat, darfWiederherstellen, onClose, onRestored }: {
  kat: 'blech' | 'profilrohr' | 'sonstiges'; darfWiederherstellen: boolean; onClose: () => void; onRestored: () => void;
}) {
  const [q, setQ] = useState('');
  const korb = useApi(() => api.blechbedarf.papierkorb({ kat, q: q.trim() || undefined }), [kat, q]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const rows: BedarfPapierkorb[] = korb.data?.data ?? [];
  async function holen(id: number) {
    setBusyId(id);
    try { await api.blechbedarf.restore(id); korb.reload(); onRestored(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusyId(null);
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
          <h2 className="font-semibold text-slate-900">🗑 Papierkorb · stornierte Bedarfe</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none" aria-label="Schließen">×</button>
        </div>
        <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <input className="input w-64" placeholder="🔍 DV, Werkstoff, Grund …" value={q} onChange={e => setQ(e.target.value)} />
          {!darfWiederherstellen && <span className="text-xs text-slate-500">Wiederherstellen dürfen nur Admin/Einkauf.</span>}
        </div>
        <div className="px-6 py-3 space-y-2 max-h-[60vh] overflow-y-auto">
          {korb.loading && <div className="text-sm text-slate-400">Lädt …</div>}
          {!korb.loading && rows.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Papierkorb ist leer.</div>}
          {rows.map(r => (
            <div key={r.id} className="border border-slate-200 rounded-lg px-3 py-2 flex items-start justify-between gap-3">
              <div className="min-w-0 text-[13px]">
                <div className="font-semibold text-slate-800">
                  #{r.id} · {r.menge ?? '—'} {r.form ?? ''} {r.werkstoff ?? ''} {r.format ?? ''} {r.dicke ? `${r.dicke} mm` : ''}
                  {r.referenz ? <span className="ml-2 text-slate-500 font-normal">DV {r.referenz}</span> : null}
                </div>
                <div className="text-slate-700">Grund: <span className="font-medium">{r.storno_grund ?? '— (vor Einführung des Storno gelöscht)'}</span></div>
                <div className="text-xs text-slate-500">{r.storniert_von_name ?? '—'} · {(r.storniert_am ?? r.deleted_at ?? '').slice(0, 16).replace('T', ' ')}</div>
              </div>
              {darfWiederherstellen && (
                <button onClick={() => holen(r.id)} disabled={busyId === r.id}
                        className="shrink-0 border border-emerald-600 text-emerald-700 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-emerald-50 disabled:opacity-50">
                  {busyId === r.id ? '…' : 'Wiederherstellen'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function F({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      {label && <label className="block text-xs text-slate-500 mb-1">{label}</label>}
      {children}
    </div>
  );
}

// Status per Klick umschalten: setzt die zugrunde liegenden Felder (Status ist
// abgeleitet, keine eigene Spalte). Gespeichert wird wie immer mit „Speichern“.
function StatusSchalter({ modal, set }: {
  modal: Partial<Blechbedarf>;
  set: (k: keyof Blechbedarf, v: string | number | null) => void;
}) {
  const heute = () => new Date().toISOString().slice(0, 10);
  const angefragt = !!((modal.lieferanten_angefragt ?? '').trim() || (modal.anfrage_datum ?? '').trim());
  const aktuell: string = modal.fertig ? 'fertig' : modal.bestellt ? 'bestellt'
    : (modal.angebote_da ?? 0) > 0 ? 'angebot' : angefragt ? 'angefragt' : 'offen';

  const wechselZu = (ziel: string) => {
    if (ziel === 'offen') {
      if (angefragt && !confirm('Zurück auf „Offen“? Anfrage-Daten (Lieferanten/Datum/Notiz) werden geleert.')) return;
      set('bestellt', 0); set('fertig', 0);
      set('lieferanten_angefragt', ''); set('anfrage_datum', ''); set('anfrage_notiz', '');
    }
    if (ziel === 'angefragt') {
      set('bestellt', 0); set('fertig', 0);
      const dat = (modal.anfrage_datum ?? '').trim() || heute();
      if (!(modal.anfrage_datum ?? '').trim()) set('anfrage_datum', dat);
      // Anfrage-Notiz vorbelegen (editierbar, z. B. für DV-Ergänzung)
      if (!(modal.anfrage_notiz ?? '').trim()) set('anfrage_notiz', `Anfrage vom ${fmtDatum(dat)}`);
    }
    if (ziel === 'bestellt') {
      set('fertig', 0); set('bestellt', 1);
      if (!(modal.bestell_datum ?? '').trim()) set('bestell_datum', heute());
    }
    if (ziel === 'fertig') {
      set('fertig', 1);
      if (!(modal.wareneingang ?? '').trim()) set('wareneingang', heute());
    }
  };

  const stufen: [string, string, string][] = [
    ['offen',     'Offen',      'bg-amber-500'],
    ['angefragt', 'Angefragt',  'bg-teal-600'],
    ['bestellt',  'Bestellt',   'bg-sky-600'],
    ['fertig',    'Angekommen', 'bg-emerald-600'],
  ];
  return (
    <div className="flex flex-wrap items-center gap-1.5 -mt-1">
      <span className="text-xs text-slate-400 uppercase mr-1">Status</span>
      {stufen.map(([wert, label, farbe]) => (
        <button key={wert} type="button" onClick={() => wechselZu(wert)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                  aktuell === wert ? `${farbe} text-white` : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
          {label}
        </button>
      ))}
      {aktuell === 'angebot' && (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-600 text-white">Angebot da</span>
      )}
      <span className="text-[11px] text-slate-400 ml-1">Klick setzt die passenden Felder — mit „Speichern“ übernehmen</span>
    </div>
  );
}

// Nachbedarf: Jürgen braucht nachträglich mehr, obwohl schon bestellt/erfasst.
// Markierung sorgt dafür, dass der Bedarf oben in der Liste auftaucht (statt
// unterzugehen) und mit Vermerk sichtbar ist, bis der Einkauf es erledigt hat.
function NachbedarfLeiste({ modal, set }: {
  modal: Partial<Blechbedarf>;
  set: (k: keyof Blechbedarf, v: string | number | null) => void;
}) {
  if (modal.nachbedarf_am) {
    return (
      <div className="flex items-center gap-2 -mt-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        <span className="text-amber-800 text-sm font-semibold">⚠ Nachbedarf gemeldet</span>
        {modal.nachbedarf_notiz && <span className="text-sm text-amber-700">— {modal.nachbedarf_notiz}</span>}
        <span className="text-xs text-amber-500 ml-1">{fmtDatum(modal.nachbedarf_am)}</span>
        <button type="button" onClick={() => { set('nachbedarf_am', null); set('nachbedarf_notiz', ''); }}
                className="ml-auto text-xs border border-amber-300 text-amber-800 rounded px-2 py-0.5 hover:bg-amber-100 whitespace-nowrap">
          ✓ erledigt
        </button>
      </div>
    );
  }
  return (
    <div className="-mt-1">
      <button type="button" onClick={() => {
        const notiz = prompt('Was wird zusätzlich gebraucht? (z. B. "noch 2 Tafeln mehr")', '');
        if (notiz === null) return;
        set('nachbedarf_am', new Date().toISOString());
        set('nachbedarf_notiz', notiz.trim() || null);
      }} className="text-xs border border-amber-300 text-amber-700 rounded px-2 py-1 hover:bg-amber-50 whitespace-nowrap">
        ➕ Nachträglich mehr gebraucht
      </button>
    </div>
  );
}
